import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Form, 
  InputNumber, 
  Select, 
  Button, 
  Card, 
  Typography, 
  Row, 
  Col,
  Tabs,
  Descriptions,
  Divider,
  Space,
  Tag,
  Statistic,
  Progress,
  Input,
  Dropdown,
  Alert,
  Switch,
  ColorPicker,
  Checkbox,
  Radio
 } from 'antd';
// export utils already imported below
import { useI18n } from '../i18n/i18n';
import { App as AntdApp } from 'antd';
import { runEngineeringChecks } from '../utils/engineeringChecks';
import { 
  CalculatorOutlined, 
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { supabase } from '../supabaseClient';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  calculateProtectionSettings, 
  getResultExplanation
} from '../utils/protectionCalculations';
import { validateInputs, getFieldRules, getFieldRange, isFieldApplicable } from '../utils/validationRules';
import { getCachedResult, cacheResult } from '../utils/cacheUtils';
import './CalculationPage.less';
import { enqueue } from '../utils/offlineQueue';
// 统一导出：复用共享工具
import { buildMarkdownReport, downloadTextFile, exportElementToPdf, exportElementToHtml, exportElementToWord, captureElementToPngDataUrl, getProtectionTypeName, getCalculationFormula, formatResultLabel, mapCurveLabel, formatResultValueExport, resolveFilenamePrefix, buildFinalBaseName, buildFormulaEvaluations } from '../utils/exportUtils';
import { getParamLabel } from '../utils/labelMappings';
// 集中枚举：统一保护类型值来源
import { PROTECTION_TYPES } from '../utils/protectionTypes';
import { createRoot } from 'react-dom/client';
import ExportReport from '../components/ExportReport';
// 修复缺失的版本戳与分享工具导入
import { listFeeders, getFeederById, computeShortCircuitForFeeder } from '../utils/deviceLibrary';
import { computeTotalsFromPerKm, applyFeederPresetToDistance } from '../utils/distanceLinkage';
import { listFeedersAsync, fetchFeederByIdAsync } from '../utils/deviceLibraryApi';
import { GROUNDING_OPTIONS, resolveGroundingImpedance } from '../utils/groundingTemplates';
import { generateVersionStamp, generateRandomToken, buildShareUrlSigned, buildShareUrlPreferShort } from '../utils/shareUtils';
import CurveOverlay from '../components/CurveOverlay';
import SegmentsEditor from '../components/SegmentsEditor';
import AutoTuningWizard from '../components/AutoTuningWizard';
import { buildSeries, analyzeSelectivity, recommendUpstreamTMS, buildSelectivityMatrix } from '../utils/tccUtils';
import { buildSelectivityMatrixMarkdown, buildSelectivityMatrixCsv } from '../utils/exportUtils';
import { PARAM_TEMPLATES, TEMPLATE_OPTIONS, DEFAULT_TEMPLATE_FOR_TYPE, validateTemplateBounds } from '../utils/paramTemplates';
import { getHistoryCacheInfo, getCurrentCalculationCache, setCurrentCalculationCache, setHistoryCache, getHistoryCache, clearHistoryCache } from '../utils/historyCache';
import { ZPlaneMini, BarChartMini } from '../components/MiniCharts';

// 功能开关：计算页“导出报告 PDF”按钮
const ENABLE_CALC_REPORT_PDF_EXPORT = true;

const { Title, Text, Paragraph } = Typography;


// 保护类型配置
const TYPE_META = {
  overcurrent: { description: '用于检测线路过载和短路故障', icon: <ThunderboltOutlined /> },
  distance: { description: '基于阻抗测量的保护原理', icon: <SafetyCertificateOutlined /> },
  differential: { description: '基于电流差动原理的快速保护', icon: <CheckCircleOutlined /> },
  directional: { description: '功率方向/角度元件判别故障方向', icon: <SafetyCertificateOutlined /> },
  zero_sequence: { description: '接地故障检测（零序电流/功率）', icon: <ThunderboltOutlined /> },
  longitudinal: { description: '线路两端比对实现快速选择性跳闸', icon: <SafetyCertificateOutlined /> }
};
// 在组件中根据当前语言生成保护类型选项

// 参数模板与默认键改为集中来源（utils/paramTemplates）


import { useNavigate, useLocation } from 'react-router-dom';

const CalculationPage = ({ userId }) => {
  const { t } = useI18n();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [calculationResult, setCalculationResult] = useState(null);
  const [activeTab, setActiveTab] = useState('input');
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedProtection, setSelectedProtection] = useState('overcurrent');
  const location = useLocation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(false);
  const [remark, setRemark] = useState('');
  const [calcName, setCalcName] = useState('');
  const [project, setProject] = useState('');
  const [line, setLine] = useState('');
  const resultRef = useRef(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  // 复制备注：是否包含项目/线路
  const [includeProjectLineInCopy, setIncludeProjectLineInCopy] = useState(false);
  // 叠加与选择性校核状态
  const [overlaySeries, setOverlaySeries] = useState([]);

  // 保护类型选项（随语言更新）
  const protectionTypes = useMemo(() => (
    PROTECTION_TYPES.map((value) => ({
      value,
      label: getProtectionTypeName(value, { t }),
      description: TYPE_META[value].description,
      icon: TYPE_META[value].icon
    }))
  ), [t]);
  // 自动整定变更日志事件（应用与回滚）
  const [wizardEvents, setWizardEvents] = useState([]);

  // 公式评估：根据当前参数与结果生成代入值与计算结果
  const formulaEvaluations = useMemo(() => (
    calculationResult ? buildFormulaEvaluations(calculationResult.parameters || {}, calculationResult.results || {}, { t }) : null
  ), [calculationResult, t]);

  // 导出选项（本地持久化）
  const [exportIncludeParamNotes, setExportIncludeParamNotes] = useState(() => {
    try {
      const raw = localStorage.getItem('exportOptions');
      if (raw) {
        const opts = JSON.parse(raw);
        if (typeof opts.includeParamNotes === 'boolean') return opts.includeParamNotes;
      }
    } catch (_) {}
    return true;
  });
  const [exportIncludeDeviceSource, setExportIncludeDeviceSource] = useState(() => {
    try {
      const raw = localStorage.getItem('exportOptions');
      if (raw) {
        const opts = JSON.parse(raw);
        if (typeof opts.includeDeviceSource === 'boolean') return opts.includeDeviceSource;
      }
    } catch (_) {}
    return true;
  });
  const [exportIncludeSelectivity, setExportIncludeSelectivity] = useState(() => {
    try {
      const raw = localStorage.getItem('exportOptions');
      if (raw) {
        const opts = JSON.parse(raw);
        if (typeof opts.includeSelectivity === 'boolean') return opts.includeSelectivity;
      }
    } catch (_) {}
    return true;
  });
  // 导出文件名前缀模板（本地持久化）
  const [exportFileNamePrefix, setExportFileNamePrefix] = useState(() => {
    try {
      const raw = localStorage.getItem('exportOptions');
      if (raw) {
        const opts = JSON.parse(raw);
        if (typeof opts.fileNamePrefix === 'string') return opts.fileNamePrefix;
      }
    } catch (_) {}
    return '{name}-{project}-{line}-{date}';
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('exportOptions');
      const prev = raw ? JSON.parse(raw) : {};
      const next = {
        ...prev,
        includeParamNotes: exportIncludeParamNotes,
        includeDeviceSource: exportIncludeDeviceSource,
        includeSelectivity: exportIncludeSelectivity,
        fileNamePrefix: exportFileNamePrefix
      };
      localStorage.setItem('exportOptions', JSON.stringify(next));
    } catch (_) {}
  }, [exportIncludeParamNotes, exportIncludeDeviceSource, exportIncludeSelectivity, exportFileNamePrefix]);

  // 字段单位映射与范围提示渲染
  const FIELD_UNIT_MAP = useMemo(() => ({
    systemVoltage: 'kV',
    ratedCurrent: 'A',
    ctRatio: '',
    ptRatio: '',
    faultCurrent: 'A',
    transformerCapacity: 'kVA',
    timeMultiplier: '',
    polarizingAngle: '°',
    k0: '',
    lineR_ohm: 'Ω',
    lineX_ohm: 'Ω',
    lineAngleDeg: '°',
    lineLength_km: 'km',
    lineR_per_km_ohm: 'Ω/km',
    lineX_per_km_ohm: 'Ω/km'
  }), []);

  const renderFieldRangeHint = (type, field) => {
    const range = getFieldRange(type, field);
    if (!range) return null;
    const [min, max] = range;
    const unit = FIELD_UNIT_MAP[field] || '';
    return `范围: ${min}~${max}${unit ? ' ' + unit : ''}`;
  };
  // 模板边界软性提示（结合 validateTemplateBounds）
  const renderTemplateBoundHint = (field) => {
    try {
      const type = selectedProtection;
      const values = form.getFieldsValue();
      const ctx = { ...values, protectionType: type };
      const { errors = [], warnings = [] } = validateTemplateBounds(ctx);
      const msgs = [];
      if (field === 'ctRatio') {
        msgs.push('模板建议: 1~20000');
        if (errors.includes('CT 变比越界或无效')) msgs.push('（当前模板越界）');
      } else if (field === 'ptRatio') {
        if (!(type === 'distance' || type === 'directional')) return null;
        msgs.push('模板建议: 1~10000');
        if (errors.includes('PT 变比越界或无效')) msgs.push('（当前模板越界）');
      } else if (field === 'timeMultiplier') {
        // 使用模板校核原文提示，保证与系统统一
        msgs.push('时间倍数 TMS 建议处于 0.05~1.5');
        if (warnings.includes('时间倍数 TMS 建议处于 0.05~1.5')) msgs.push('（超出建议范围）');
        // 并入输入软校核的提示：接近上限
        try {
          const { warnings: inputWarns = [] } = validateInputs(ctx);
          if (Array.isArray(inputWarns) && inputWarns.includes('时间倍数 TMS 接近上限，可能影响选择性')) {
            msgs.push('（接近上限，可能影响选择性）');
          }
        } catch (_) {}
      } else if (field === 'faultCurrent') {
        const rc = Number(values?.ratedCurrent);
        const fc = Number(values?.faultCurrent);
        if (isFinite(rc) && isFinite(fc) && rc > 0) {
          const ratio = fc / rc;
          const text = ratio < 10 ? ratio.toFixed(2) : String(Math.round(ratio));
          msgs.push(`建议≥额定的1.2×，当前≈${text}×`);
          if (ratio < 1.2) msgs.push('（偏低）');
        } else {
          msgs.push('建议≥额定的1.2×');
        }
      } else {
        return null;
      }
      return msgs.length > 0 ? msgs.join(' · ') : null;
    } catch (_) {
      // 回退静态提示
      if (field === 'ctRatio') return '模板建议: 1~20000';
      if (field === 'ptRatio') return (selectedProtection === 'distance' || selectedProtection === 'directional') ? '模板建议: 1~10000' : null;
      if (field === 'timeMultiplier') return '时间倍数 TMS 建议处于 0.05~1.5';
      if (field === 'faultCurrent') return '建议≥额定的1.2×';
      return null;
    }
  };
  // 合并范围提示与模板建议
  const renderCombinedHint = (field) => {
    const rangeText = renderFieldRangeHint(selectedProtection, field);
    const tplText = renderTemplateBoundHint(field);
    if (rangeText && tplText) return `${rangeText} ｜ ${tplText}`;
    return rangeText || tplText || null;
  };
  // 即时提示：将关键字段的合并提示以消息形式弹出
  const notifyKeyFieldHints = () => {
    try {
      const fields = ['ctRatio', 'ptRatio', 'timeMultiplier', 'faultCurrent'];
      const labelMap = {
        ctRatio: 'CT变比',
        ptRatio: 'PT变比',
        timeMultiplier: '时间倍数 (TMS)',
        faultCurrent: '故障电流'
      };
      const lines = fields
        .map((f) => {
          const text = renderCombinedHint(f);
          return text ? `${labelMap[f]}：${text}` : null;
        })
        .filter(Boolean);

      if (!lines.length) return;

      message.open({
        key: 'keyFieldHints',
        type: 'info',
        content: (
          <div>
            <div style={{ fontWeight: 500 }}>关键字段提示</div>
            {lines.map((l, idx) => (
              <div key={idx}>{l}</div>
            ))}
          </div>
        ),
        duration: 5
      });
    } catch (_) {}
  };
  const [historyCandidates, setHistoryCandidates] = useState([]);
  // 避免重复打印网络错误日志（如开发环境下的 HMR 中断、离线）
  const overlayFetchErrorRef = useRef(false);
  const [overlayShowLegend, setOverlayShowLegend] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? !!JSON.parse(s).showLegend : true; } catch { return true; }
  });
  const [overlayZoomX, setOverlayZoomX] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? (JSON.parse(s).zoomX || 1) : 1; } catch { return 1; }
  });
  const [overlayZoomY, setOverlayZoomY] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? (JSON.parse(s).zoomY || 1) : 1; } catch { return 1; }
  });
  const [overlayPanX, setOverlayPanX] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? (JSON.parse(s).panX || 0) : 0; } catch { return 0; }
  });
  const [overlayPanY, setOverlayPanY] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? (JSON.parse(s).panY || 0) : 0; } catch { return 0; }
  });
  const [overlayColorMap, setOverlayColorMap] = useState(() => {
    try { const s = localStorage.getItem('tccOverlayColors'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [overlayHiddenLabels, setOverlayHiddenLabels] = useState(() => {
    try { const s = localStorage.getItem('tccOverlayHidden'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [overlayTooltipsEnabled, setOverlayTooltipsEnabled] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? !!JSON.parse(s).showTooltips : true; } catch { return true; }
  });
  const [overlayBoxZoomEnabled, setOverlayBoxZoomEnabled] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? !!JSON.parse(s).enableBoxZoom : true; } catch { return true; }
  });
  const [overlayColorBlindMode, setOverlayColorBlindMode] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); return s ? !!JSON.parse(s).colorBlindMode : false; } catch { return false; }
  });
  const [overlayRefLineKeys, setOverlayRefLineKeys] = useState(() => {
    try { const s = localStorage.getItem('tccOverlayRefLines'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [warningFilter, setWarningFilter] = useState('all');
  const [overlayLabelOverrides, setOverlayLabelOverrides] = useState(() => {
    try { const s = localStorage.getItem('tccOverlayLabels'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  // 选择性校核：最小裕度（全局与每对）
  const [overlayMinMargin, setOverlayMinMargin] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); const j = s ? JSON.parse(s) : {}; return typeof j.minMargin === 'number' ? j.minMargin : 0.3; } catch { return 0.3; }
  });
  const [overlayMinMargins, setOverlayMinMargins] = useState(() => {
    try { const s = localStorage.getItem('tccOverlaySettings'); const j = s ? JSON.parse(s) : {}; return Array.isArray(j.minMargins) ? j.minMargins : []; } catch { return []; }
  });
  // 选择性校核面板（多层链路选择）——与叠加解耦的面板状态与持久化
  const [selPanelEnabled, setSelPanelEnabled] = useState(() => {
    try { const s = localStorage.getItem('tccSelectivityPanel'); const j = s ? JSON.parse(s) : {}; return !!j.enabled; } catch { return false; }
  });
  const [selPanelOrder, setSelPanelOrder] = useState(() => {
    try { const s = localStorage.getItem('tccSelectivityPanel'); const j = s ? JSON.parse(s) : {}; return Array.isArray(j.seriesOrder) ? j.seriesOrder : []; } catch { return []; }
  });
  const [selPanelGlobalMinMargin, setSelPanelGlobalMinMargin] = useState(() => {
    try { const s = localStorage.getItem('tccSelectivityPanel'); const j = s ? JSON.parse(s) : {}; return typeof j.globalMinMargin === 'number' ? j.globalMinMargin : (typeof overlayMinMargin === 'number' ? overlayMinMargin : 0.3); } catch { return (typeof overlayMinMargin === 'number' ? overlayMinMargin : 0.3); }
  });
  const [selPanelMinMargins, setSelPanelMinMargins] = useState(() => {
    try { const s = localStorage.getItem('tccSelectivityPanel'); const j = s ? JSON.parse(s) : {}; return Array.isArray(j.minMargins) ? j.minMargins : []; } catch { return []; }
  });
  // 面板交互增强：临时曲线选择器（用于追加到面板顺序）
  const [selPanelPicker, setSelPanelPicker] = useState([]);
  const overlayContainerRef = useRef(null);
  const lastOfflineWriteRef = useRef(0);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  // 自动整定向导状态
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialValues, setWizardInitialValues] = useState(null);
  const [wizardRestoreValues, setWizardRestoreValues] = useState(null);
  // 分享策略（短链优先与策略门控）
  const [sharePreferShort, setSharePreferShort] = useState(true);
  const [shareRequiresLogin, setShareRequiresLogin] = useState(false);
  const [shareMaxUses, setShareMaxUses] = useState(1);
const [feederOptions, setFeederOptions] = useState([]);
const [selectedFeederObj, setSelectedFeederObj] = useState(null);
// 将字段监听移入表单上下文，避免 useForm 未连接的警告
const [scInputs, setScInputs] = useState({ selectedFeederId: undefined, motorPercent: undefined, groundingType: undefined });
const scResult = useMemo(() => {
  const fid = scInputs?.selectedFeederId;
  const mp = scInputs?.motorPercent ?? 0;
  const gt = scInputs?.groundingType;
  const f = selectedFeederObj || (fid ? getFeederById(fid) : null);
  if (!f) return null;
  return computeShortCircuitForFeeder(f, { motor_percent: mp || 0, groundingType: gt });
}, [selectedFeederObj, scInputs?.selectedFeederId, scInputs?.motorPercent, scInputs?.groundingType]);

  // 从设备库对象推断接地类型（优先使用变压器中性点记录，其次依据阻抗近似推断）
  const inferGroundingTypeFromFeeder = (f) => {
    try {
      const t = f?.transformer?.secondary_neutral_ground;
      if (t && typeof t === 'string') return t;
      const R = Number(f?.ground?.R_ohm);
      const X = Number(f?.ground?.X_ohm);
      const hasR = isFinite(R);
      const hasX = isFinite(X);
      if (!hasR && !hasX) return undefined;
      if ((hasR && R < 0.05) && (hasX && X < 0.05)) return 'solid';
      if ((hasR && R > 1e5) || (hasX && X > 1e5)) return 'none';
      if (hasX && hasR && X >= (R * 2) && X > 1.0) return 'reactor';
      if (hasR && R >= 0.2) return 'resistor';
      return 'solid';
    } catch (_) {
      return undefined;
    }
  };

  // 基于馈线线路R/X推导方向保护极化角的建议值（典型取 30° 或 45°）
  const suggestPolarizingAngleFromFeeder = (f) => {
    try {
      const Rpkm = f?.line?.R_per_km_ohm ?? f?.R_per_km_ohm;
      const Xpkm = f?.line?.X_per_km_ohm ?? f?.X_per_km_ohm;
      const Lkm = f?.line?.length_km ?? f?.length_km ?? 1;
      let angle;
      if (isFinite(Rpkm) && isFinite(Xpkm)) {
        const R = (Number(Rpkm) || 0) * (Number(Lkm) || 0);
        const X = (Number(Xpkm) || 0) * (Number(Lkm) || 0);
        const ang = Math.atan2(X, R) * 180 / Math.PI;
        if (isFinite(ang)) angle = parseFloat(Number(ang).toFixed(1));
      }
      if (!(typeof angle === 'number' && isFinite(angle))) angle = 30;
      const candidates = [30, 45];
      const pick = candidates.reduce((a, b) => (Math.abs(angle - a) <= Math.abs(angle - b) ? a : b));
      return Math.min(60, Math.max(20, pick));
    } catch (_) {
      return 30;
    }
  };

  // 初始化表单默认参数
  useEffect(() => {
    form.setFieldsValue({
      systemVoltage: 10.5,
      ratedCurrent: 1000,
      ctRatio: 600,
      ptRatio: 100,
      faultCurrent: 8000,
      protectionType: 'overcurrent',
      transformerCapacity: 20000,
      curveType: 'Normal Inverse',
      timeMultiplier: 0.5,
      polarizingAngle: 30,
      k0: 0.3,
      groundingType: 'solid'
    });
  }, [form]);

  // 设备库选项加载（远端优先，失败回退本地）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await listFeedersAsync();
        if (!cancelled && Array.isArray(opts) && opts.length) {
          setFeederOptions(opts);
        } else if (!cancelled) {
          setFeederOptions(listFeeders());
        }
      } catch (_) {
        if (!cancelled) setFeederOptions(listFeeders());
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 从历史记录还原参数与结果
  useEffect(() => {
    const state = location?.state;
    // 记录当前记录ID（用于更新而非新增）
    if (state?.record?.id) {
      setCurrentRecordId(state.record.id);
    } else {
      setCurrentRecordId(null);
    }
    if (state?.record) {
      const { parameters, results, calculation_type } = state.record;
      if (parameters) {
        form.setFieldsValue(parameters);
      }
      const pt = parameters?.protectionType || calculation_type || 'overcurrent';
      let restoredResults = results;
      if (!restoredResults && parameters) {
        try {
          restoredResults = calculateProtectionSettings(parameters);
        } catch (_) {
          restoredResults = null;
        }
      }
      if (parameters && restoredResults) {
        setCalculationResult({
          parameters,
          results: restoredResults,
          protectionTypeName: protectionTypes.find(t => t.value === pt)?.label
        });
        setSelectedProtection(pt);
        setActiveTab('result');
      }
      if (state.record.result?.remark) {
        setRemark(state.record.result.remark);
      } else {
        setRemark('');
      }
      if (state.record.result?.name) {
        setCalcName(state.record.result.name);
      } else {
        setCalcName('');
      }
      try {
        const ex = state.record.result?.extras || {};
        setProject((ex.project || '').trim());
        setLine((ex.line || '').trim());
        // 从记录的extras恢复叠加视图与选择性面板
        if (ex.overlay) {
          const ov = ex.overlay || {};
          const settings = ov.settings || {};
          try {
            if (ov.colorMap) setOverlayColorMap(ov.colorMap);
            if (Array.isArray(ov.hiddenLabels)) setOverlayHiddenLabels(ov.hiddenLabels);
            if (typeof settings.showLegend === 'boolean') setOverlayShowLegend(settings.showLegend);
            if (typeof settings.zoomX === 'number') setOverlayZoomX(settings.zoomX);
            if (typeof settings.zoomY === 'number') setOverlayZoomY(settings.zoomY);
            if (typeof settings.panX === 'number') setOverlayPanX(settings.panX);
            if (typeof settings.panY === 'number') setOverlayPanY(settings.panY);
            if (typeof settings.showTooltips === 'boolean') setOverlayTooltipsEnabled(settings.showTooltips);
            if (typeof settings.enableBoxZoom === 'boolean') setOverlayBoxZoomEnabled(settings.enableBoxZoom);
            if (typeof settings.colorBlindMode === 'boolean') setOverlayColorBlindMode(settings.colorBlindMode);
            if (Array.isArray(settings.refLineKeys)) setOverlayRefLineKeys(settings.refLineKeys);
            if (typeof ov.minMargin === 'number') setOverlayMinMargin(ov.minMargin);
            if (Array.isArray(ov.minMargins)) setOverlayMinMargins(ov.minMargins);
          } catch (_) {}
          // 重建叠加序列并按选择性面板顺序排序（若有）
          try {
            const defs = Array.isArray(ov.defs) ? ov.defs : [];
            const series = defs.map(d => {
              const label = d.label || d.key;
              const color = (ov.colorMap?.[d.key] || ov.colorMap?.[d.label] || d.color || '#1677ff');
              const results = {
                axesType: d.axesType,
                curveType: d.curveType,
                pickupCurrent: d.pickup ?? d.pickupCurrent,
                timeMultiplier: d.TMS ?? d.timeMultiplier,
                zones: d.zones,
                characteristic: d.characteristic
              };
              const s = buildSeries({ label, color, results });
              s.key = d.key || label;
              return s;
            });
            let ordered = series;
            if (ex.selectivityPanel && Array.isArray(ex.selectivityPanel.seriesOrder) && ex.selectivityPanel.seriesOrder.length) {
              const map = new Map(series.map(s => [s.key || s.label, s]));
              ordered = ex.selectivityPanel.seriesOrder.map(k => map.get(k)).filter(Boolean);
            }
            if (ordered && ordered.length) setOverlaySeries(ordered);
          } catch (_) {}
        }
        if (ex.selectivityPanel && ex.selectivityPanel.enabled) {
          try {
            setSelPanelEnabled(true);
            setSelPanelOrder(Array.isArray(ex.selectivityPanel.seriesOrder) ? ex.selectivityPanel.seriesOrder : []);
            if (typeof ex.selectivityPanel.globalMinMargin === 'number') setSelPanelGlobalMinMargin(ex.selectivityPanel.globalMinMargin);
            if (Array.isArray(ex.selectivityPanel.minMargins)) setSelPanelMinMargins(ex.selectivityPanel.minMargins);
          } catch (_) {}
        }
      } catch (_) {
        setProject('');
        setLine('');
      }
      if (state.exportImmediately && parameters && restoredResults) {
        setTimeout(() => {
          handleExportReport();
        }, 0);
      }
      if (state.exportPdfImmediately && parameters && restoredResults) {
        setTimeout(() => {
          handleExportPDF();
        }, 0);
      }
    }
    setViewMode(state?.mode === 'view');
  }, [location?.state]);

  // 从数据库获取用于叠加的历史候选
  useEffect(() => {
    const useCacheFallback = () => {
      try {
        const info = getHistoryCacheInfo();
        setHistoryCandidates(info.list || []);
      } catch (_) {
        setHistoryCandidates([]);
      }
    };

    // 未登录：回退到本地缓存
    if (!userId) {
      useCacheFallback();
      return;
    }

    const fetchCandidates = async () => {
      try {
        // 离线：直接使用本地缓存
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          useCacheFallback();
          return;
        }
        const { data, error } = await supabase
          .from('calculations_23813')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(30);
        if (error) throw error;
        setHistoryCandidates(data || []);
      } catch (err) {
        // 对常见的网络中断/离线错误降级为一次性警告，并兜底使用本地缓存
        const msg = String(err?.message || err || '')
        const isAbort = err?.name === 'AbortError' || /ERR_ABORTED/i.test(msg);
        const isNetworkFail = /Failed to fetch/i.test(msg) || /TypeError: Failed to fetch/i.test(msg);
        if (!overlayFetchErrorRef.current) {
          if (isAbort || isNetworkFail) {
            console.warn('获取叠加候选失败（网络/中断）：已回退到本地缓存');
          } else {
            console.warn('获取叠加候选失败：', err);
          }
          overlayFetchErrorRef.current = true;
        }
        // 异常：使用本地缓存兜底
        useCacheFallback();
      }
    };
    fetchCandidates();
  }, [userId]);

  // 颜色映射随 series 更新时补齐，并持久化
  useEffect(() => {
    const next = { ...overlayColorMap };
    let changed = false;
    (overlaySeries || []).forEach((s, idx) => {
      const label = s.label;
      const color = s.color;
      if (label && color && next[label] !== color) { next[label] = color; changed = true; }
    });
    if (changed) {
      setOverlayColorMap(next);
      try { localStorage.setItem('tccOverlayColors', JSON.stringify(next)); } catch {}
    }
  }, [overlaySeries]);

  useEffect(() => {
    try { localStorage.setItem('tccOverlayHidden', JSON.stringify(overlayHiddenLabels)); } catch {}
  }, [overlayHiddenLabels]);

  useEffect(() => {
    try { localStorage.setItem('tccOverlayRefLines', JSON.stringify(overlayRefLineKeys)); } catch {}
  }, [overlayRefLineKeys]);

  // 合并持久化叠加设置，避免覆盖其他设置项
  const mergeOverlaySettings = (patch) => {
    try {
      const saved = localStorage.getItem('tccOverlaySettings');
      const prev = saved ? JSON.parse(saved) : {};
      const next = { ...prev, ...patch };
      localStorage.setItem('tccOverlaySettings', JSON.stringify(next));
    } catch {}
  };

  // 收集叠加视图与选择性面板的快照（用于保存与离线历史）
  const collectOverlayAndSelectivityExtras = () => {
    try {
      const overlayDefs = (overlaySeries || []).map(s => ({
        key: s.key || s.label,
        label: s.label,
        color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color),
        pickup: s.pickup ?? s.results?.pickupCurrent,
        TMS: s.TMS ?? s.results?.timeMultiplier,
        curveType: s.curveType ?? s.results?.curveType,
        axesType: s.axesType ?? s.results?.axesType,
        zones: s.meta?.zones ?? s.results?.zones,
        characteristic: s.meta?.characteristic ?? s.results?.characteristic
      }));
      const overlay = overlayDefs.length > 0 ? {
        defs: overlayDefs,
        settings: {
          showLegend: overlayShowLegend,
          zoomX: overlayZoomX,
          zoomY: overlayZoomY,
          panX: overlayPanX,
          panY: overlayPanY,
          showTooltips: overlayTooltipsEnabled,
          enableBoxZoom: overlayBoxZoomEnabled,
          colorBlindMode: overlayColorBlindMode,
          refLineKeys: overlayRefLineKeys
        },
        colorMap: overlayColorMap,
        hiddenLabels: overlayHiddenLabels,
        minMargin: overlayMinMargin,
        minMargins: overlayMinMargins
  } : undefined;

      const selectivityPanel = selPanelEnabled ? {
        enabled: true,
        seriesOrder: selPanelOrder,
        globalMinMargin: selPanelGlobalMinMargin,
        minMargins: selPanelMinMargins
      } : undefined;

      // 选择性矩阵（若可用）
      let selectivityMatrix;
      try {
        const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
        const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
        const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
          ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
          : visible;
        if (seriesForMatrix.length >= 2) {
          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
          selectivityMatrix = { ...matrix, minMargin: mmForMatrix };
        }
      } catch (_) {}

      return { overlay, selectivityPanel, selectivityMatrix };
    } catch (_) {
      return {};
    }
  };

  // 持久化最小裕度（全局）
  useEffect(() => {
    mergeOverlaySettings({ minMargin: overlayMinMargin });
  }, [overlayMinMargin]);

  // 持久化最小裕度（每对）
  useEffect(() => {
    mergeOverlaySettings({ minMargins: overlayMinMargins });
  }, [overlayMinMargins]);

  // 面板状态持久化
  useEffect(() => {
    try {
      const payload = {
        enabled: selPanelEnabled,
        seriesOrder: selPanelOrder,
        globalMinMargin: selPanelGlobalMinMargin,
        minMargins: selPanelMinMargins
      };
      localStorage.setItem('tccSelectivityPanel', JSON.stringify(payload));
    } catch {}
  }, [selPanelEnabled, selPanelOrder, selPanelGlobalMinMargin, selPanelMinMargins]);

  // 当叠加序列新增时应用重命名覆盖
  useEffect(() => {
    if (!overlaySeries || overlaySeries.length === 0) return;
    const map = overlayLabelOverrides || {};
    const updated = overlaySeries.map(s => {
      const k = s.key || s.label;
      if (k && map[k] && map[k] !== s.label) {
        return { ...s, label: map[k] };
      }
      return s;
    });
    // 仅当存在差异时更新，避免死循环
    const changed = updated.some((s, i) => s.label !== overlaySeries[i].label);
    if (changed) setOverlaySeries(updated);
  }, [overlaySeries, overlayLabelOverrides]);

  // 添加当前计算结果的曲线到叠加
  const addCurrentSeries = () => {
    try {
      if (!calculationResult) {
        message.warning('请先进行计算');
        return;
      }
      // 放宽限制：任何包含可绘图数据(含 axesType)的结果均可叠加
      const res = calculationResult.results || {};
      if (!res.axesType) {
        message.warning('当前结果不包含可叠加的曲线或图形');
        return;
      }
      const label = (calcName && calcName.trim()) ? calcName.trim() : '当前曲线';
      const key = String(currentRecordId ?? `local:${Date.now()}`);
      const serie = buildSeries({ label, color: '#1677ff', results: res });
      Object.assign(serie, { key });
      // 应用重命名映射（若有）
      try {
        const map = overlayLabelOverrides || JSON.parse(localStorage.getItem('tccOverlayLabels') || '{}') || {};
        const override = map[key];
        if (override && typeof override === 'string') { serie.label = override; }
      } catch {}
      setOverlaySeries(prev => [...prev, serie]);
    } catch (err) {
      console.error('添加当前曲线失败:', err);
      message.error('添加当前曲线失败');
    }
  };

  // 从历史记录添加曲线到叠加
  const addHistorySeries = async (id) => {
    try {
      if (id === 'local_current') {
        const rec = getCurrentCalculationCache();
        if (!rec) {
          message.warning('本地当前计算不存在');
          return;
        }
        const name = (rec.result?.name && rec.result.name.trim()) ? rec.result.name.trim() : '当前计算（本地缓存）';
        let results = rec?.results;
        if ((!results || !results.axesType) && rec.parameters) {
          try { results = calculateProtectionSettings(rec.parameters); } catch (_) {}
        }
        if (!results?.axesType) {
          message.warning('本地当前计算缺少可叠加的曲线或图形参数');
          return;
        }
        const color = '#faad14';
        const key = 'local_current';
        const serie = buildSeries({ label: name, color, results });
        Object.assign(serie, { key });
        try {
          const map = overlayLabelOverrides || JSON.parse(localStorage.getItem('tccOverlayLabels') || '{}') || {};
          const override = map[key];
          if (override && typeof override === 'string') { serie.label = override; }
        } catch {}
        setOverlaySeries(prev => [...prev, serie]);
        return;
      }
      const rec = (historyCandidates || []).find(r => r.id === id);
      if (!rec) {
        message.warning('未找到该历史记录');
        return;
      }
      const name = (rec.result?.name && rec.result.name.trim()) ? rec.result.name.trim() : `记录 ${String(rec.id || '').slice(0, 8)}`;
      // 统一结果结构：优先使用记录中的 results；若不完整则重算
      let results = rec?.results;
      if ((!results || !results.axesType) && rec.parameters) {
        try {
          // 重算完整结果以便叠加（支持多保护类型）
          results = calculateProtectionSettings(rec.parameters);
        } catch (e) {
          // 静默失败，后续判定缺参
        }
      }

      if (!results?.axesType) {
        message.warning('该历史记录缺少可叠加的曲线或图形参数');
        return;
      }

      const color = '#52c41a';
      const key = String(rec.id);
      const serie = buildSeries({ label: name, color, results });
      Object.assign(serie, { key });
      // 应用重命名映射（若有）
      try {
        const map = overlayLabelOverrides || JSON.parse(localStorage.getItem('tccOverlayLabels') || '{}') || {};
        const override = map[key];
        if (override && typeof override === 'string') { serie.label = override; }
      } catch {}
      setOverlaySeries(prev => [...prev, serie]);
    } catch (err) {
      console.error('从历史记录添加曲线失败:', err);
      message.error('添加历史曲线失败');
    }
  };

  // 清空叠加
  const clearOverlay = () => {
    setOverlaySeries([]);
  };

  // 自动整定：打开向导并记录快照
  const openAutoWizard = () => {
    try {
      const values = form.getFieldsValue();
      setWizardInitialValues(values);
      setWizardRestoreValues(values);
      setWizardOpen(true);
    } catch (_) {
      setWizardOpen(true);
    }
  };

  // 自动整定：应用建议并触发计算
  const applyWizardSuggestions = (payload) => {
    try {
      const suggestions = (payload && typeof payload === 'object' && payload.suggestions) ? payload.suggestions : payload;
      const meta = (payload && typeof payload === 'object' && payload.meta) ? payload.meta : {};
      const patch = {};
      Object.entries(suggestions || {}).forEach(([k, v]) => { patch[k] = v; });
      if (Object.keys(patch).length > 0) {
        form.setFieldsValue(patch);
        message.success('已应用自动整定建议');
        // 记录变更事件
        try {
          const prevValues = wizardRestoreValues || form.getFieldsValue();
          const changes = Object.entries(suggestions || {})
            .map(([k, to]) => ({
              key: k,
              label: getParamLabel(k, selectedProtection),
              from: prevValues?.[k],
              to
            }))
            .filter(c => JSON.stringify(c.from) !== JSON.stringify(c.to));
          const evt = {
            action: 'apply',
            time: new Date().toISOString(),
            type: selectedProtection,
            source: 'auto_tuning_wizard',
            goal: meta?.goal,
            appliedFields: Array.isArray(meta?.appliedFields) ? meta.appliedFields : (Object.keys(suggestions || {})),
            rationale: Array.isArray(meta?.rationale) ? meta.rationale : [],
            warnings: Array.isArray(meta?.warnings) ? meta.warnings : [],
            userAdjustments: meta?.userAdjustments || {},
            changes
          };
          setWizardEvents(prev => [...prev, evt]);
        } catch (_) {}
      } else {
        message.info('未检测到可应用的建议');
      }
    } catch (_) {}
    setWizardOpen(false);
    // 应用后自动重新计算
    setTimeout(() => { try { handleCalculate(); } catch (_) {} }, 0);
  };

  // 自动整定：回滚至快照并触发计算
  const rollbackWizard = () => {
    try {
      if (!wizardRestoreValues) {
        message.info('暂无可回滚的快照');
        return;
      }
      const currentValues = form.getFieldsValue();
      // 计算回滚差异（仅记录变更的字段）
      const reverted = Object.keys(wizardRestoreValues || {})
        .map(k => ({ key: k, label: getParamLabel(k, selectedProtection), from: currentValues?.[k], to: wizardRestoreValues?.[k] }))
        .filter(c => JSON.stringify(c.from) !== JSON.stringify(c.to));
      form.setFieldsValue(wizardRestoreValues);
      message.success('已回滚至向导前参数');
      try {
        setWizardEvents(prev => [...prev, {
          action: 'rollback',
          time: new Date().toISOString(),
          type: selectedProtection,
          source: 'auto_tuning_wizard',
          reverted
        }]);
      } catch (_) {}
      setTimeout(() => { try { handleCalculate(); } catch (_) {} }, 0);
  } catch (_) {}
  };

  // 从自动整定事件构建参数变更摘要（用于导出/分享）
  const buildParamChangeSummary = (events = []) => {
    try {
      const applyCount = events.filter(e => e?.action === 'apply').length;
      const rollbackCount = events.filter(e => e?.action === 'rollback').length;
      const lastEvt = events.length > 0 ? events[events.length - 1] : null;
      const lastAction = lastEvt?.action || null;
      const lastTime = lastEvt?.time || null;
      const seen = new Set();
      const changedParams = [];
      for (let i = events.length - 1; i >= 0; i--) {
        const e = events[i] || {};
        const diffs = (Array.isArray(e.changes) && e.changes.length > 0) ? e.changes : (Array.isArray(e.reverted) ? e.reverted : []);
        diffs.forEach(d => {
          const key = d?.key;
          if (!key || seen.has(key)) return;
          seen.add(key);
          changedParams.push({
            key,
            label: d?.label || getParamLabel(key, e?.type || selectedProtection),
            to: d?.to
          });
        });
      }
      const rationale = Array.from(new Set(events.flatMap(e => (Array.isArray(e?.rationale) ? e.rationale : []))));
      const warnings = Array.from(new Set(events.flatMap(e => (Array.isArray(e?.warnings) ? e.warnings : []))));
      return { applyCount, rollbackCount, lastAction, lastTime, changedParams: changedParams.reverse(), rationale, warnings };
    } catch (_) {
      return null;
    }
  };

  // 设备边界校核：基于选定馈线与表单值进行一致性检查（含阻断项）
  const buildDeviceBoundaryChecks = (values) => {
    const warnings = [];
    const blockers = [];
    try {
      const feederId = values?.selectedFeederId || form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      if (!f) return { warnings, blockers };
      const toNum = (x) => (typeof x === 'number' ? x : undefined);
      const relDiff = (a, b) => {
        const na = Number(a), nb = Number(b);
        if (!isFinite(na) || !isFinite(nb) || nb === 0) return null;
        return Math.abs(na - nb) / Math.abs(nb);
      };

      // 系统电压 vs 母线电压
      const sv = toNum(values?.systemVoltage);
      const fv = toNum(f?.bus?.voltage_kV);
      if (sv != null && fv != null) {
        const d = relDiff(sv, fv);
        if (d != null) {
          if (d > 0.10) {
            blockers.push(`系统电压(${sv}kV)与馈线母线电压(${fv}kV)偏差>10%，请纠正电压等级后再计算`);
          } else if (d > 0.05) {
            warnings.push(`系统电压(${sv}kV)与馈线母线电压(${fv}kV)偏差>5%，建议校核电压等级`);
          }
        }
      }

      // 额定电流 vs 馈线额定
      const ir = toNum(values?.ratedCurrent);
      const fr = toNum(f?.ratedCurrent);
      if (ir != null && fr != null) {
        const d = relDiff(ir, fr);
        if (d != null) {
          if (d > 0.5) {
            blockers.push(`额定电流(${ir}A)与馈线额定值(${fr}A)偏差>50%，请核对负荷与CT选型`);
          } else if (d > 0.2) {
            warnings.push(`额定电流(${ir}A)与馈线额定值(${fr}A)偏差>20%，请核对负荷与CT选型`);
          }
        }
      }

      // CT 变比一致性
      const ct = toNum(values?.ctRatio);
      const fct = toNum(f?.ctRatio);
      if (ct != null && fct != null) {
        const d = relDiff(ct, fct);
        if (d != null) {
          if (d > 0.3) {
            blockers.push(`CT变比与设备库记录偏差>30%（当前1:${ct}，库1:${fct}），请确认一次/二次改造或选择正确馈线`);
          } else if (ct !== fct) {
            warnings.push(`CT变比与设备库记录不一致（当前1:${ct}，库1:${fct}），请确认一次/二次改造或选择正确馈线`);
          }
        }
      }

      // 接地方式一致性（与设备库变压器中性点）
      const gsel = values?.groundingType;
      const fgn = f?.transformer?.secondary_neutral_ground;
      if (gsel && fgn && gsel !== fgn) {
        blockers.push(`接地类型与设备库变压器中性点接地不一致（当前${gsel}，库${fgn}），短路电流模型不匹配`);
      }

      // 故障电流 vs 短路计算最大值
      const ifault = toNum(values?.faultCurrent);
      try {
        const sc = computeShortCircuitForFeeder(f, { motor_percent: values?.motorPercent || 0, groundingType: values?.groundingType });
        const ikmax = toNum(sc?.three_phase?.Ik3p_max_A);
        if (ifault != null && ikmax != null && ikmax > 0) {
          const d = relDiff(ifault, ikmax);
          if (d != null) {
            if (d > 0.3) {
              blockers.push(`故障电流(${ifault}A)与短路计算最大值(${Math.round(ikmax)}A)偏差>30%，请使用短路计算结果或修正输入`);
            } else if (d > 0.2) {
              warnings.push(`故障电流(${ifault}A)与短路计算最大值(${Math.round(ikmax)}A)偏差>20%，建议使用短路计算结果`);
            }
          }
        }
      } catch (_) {}

      // 距离保护：线路R/X总值与设备库预期一致性
      if (values?.protectionType === 'distance') {
        const Rpkm = f?.line?.R_per_km_ohm;
        const Xpkm = f?.line?.X_per_km_ohm;
        const Lkm = f?.line?.length_km;
        if (typeof Rpkm === 'number' && typeof Xpkm === 'number' && typeof Lkm === 'number') {
          const RtotF = Rpkm * Lkm;
          const XtotF = Xpkm * Lkm;
          const Rcur = toNum(values?.lineR_ohm);
          const Xcur = toNum(values?.lineX_ohm);
          if (Rcur != null) {
            const dR = relDiff(Rcur, RtotF);
            if (dR != null) {
              if (dR > 0.25) {
                blockers.push(`线路电阻总值与设备库偏差>25%（当前${Rcur}Ω，库约${RtotF.toFixed(4)}Ω），请核对长度与单位阻抗`);
              } else if (dR > 0.15) {
                warnings.push(`线路电阻总值与设备库不一致（当前${Rcur}Ω，库约${RtotF.toFixed(4)}Ω），请核对长度与单位阻抗`);
              }
            }
          }
          if (Xcur != null) {
            const dX = relDiff(Xcur, XtotF);
            if (dX != null) {
              if (dX > 0.25) {
                blockers.push(`线路电抗总值与设备库偏差>25%（当前${Xcur}Ω，库约${XtotF.toFixed(4)}Ω），请核对长度与单位阻抗`);
              } else if (dX > 0.15) {
                warnings.push(`线路电抗总值与设备库不一致（当前${Xcur}Ω，库约${XtotF.toFixed(4)}Ω），请核对长度与单位阻抗`);
              }
            }
          }
        }
      }
    } catch (_) {}
    return { warnings, blockers };
  };

  const handleCalculate = () => {
    form.validateFields().then(values => {
      setIsCalculating(true);
      
      // 验证输入
  const { valid, errors, warnings: inputWarnings } = validateInputs(values);
  
  if (!valid) {
    // 显示验证错误
    errors.forEach(error => {
      message.error(error);
    });
    setIsCalculating(false);
    return;
  }
  if (Array.isArray(inputWarnings) && inputWarnings.length > 0) {
    inputWarnings.forEach(w => message.warning(w));
  }
  // 设备边界校核（含阻断项）
  const { warnings: deviceWarnings, blockers: deviceBlockers } = buildDeviceBoundaryChecks(values);
  if (Array.isArray(deviceWarnings) && deviceWarnings.length > 0) {
    deviceWarnings.forEach(w => message.warning(w));
  }
  if (Array.isArray(deviceBlockers) && deviceBlockers.length > 0) {
    deviceBlockers.forEach(b => message.error(b));
    setIsCalculating(false);
    return;
  }
  // 模板边界校验（含阻断项）
  let tplWarnings = [];
  try {
    const res = validateTemplateBounds(values);
    tplWarnings = Array.isArray(res?.warnings) ? res.warnings : [];
    const tplErrors = Array.isArray(res?.errors) ? res.errors : [];
    if (tplWarnings.length > 0) tplWarnings
      .filter(w => w !== '时间倍数 TMS 建议处于 0.05~1.5')
      .forEach(w => message.warning(w));
    if (tplErrors.length > 0) {
      tplErrors.forEach(e => message.error(e));
      setIsCalculating(false);
      return;
    }
  } catch (_) {}
      
      // 检查缓存中是否有结果
      const cachedResults = getCachedResult(values);
      if (cachedResults) {
        const { warnings: engWarnings, blockers } = runEngineeringChecks(values, cachedResults);
        if (Array.isArray(blockers) && blockers.length > 0) {
          blockers.forEach(b => message.error(b));
          setIsCalculating(false);
          return;
        }
        setCalculationResult({
          parameters: values,
          results: cachedResults,
          protectionTypeName: protectionTypes.find(t => t.value === values.protectionType)?.label,
          engineeringWarnings: [...(inputWarnings || []), ...(engWarnings || []), ...(tplWarnings || [])],
          deviceWarnings: deviceWarnings || [],
          deviceBlockers: deviceBlockers || []
        });
        try {
          setCurrentCalculationCache({
            id: 'local_current',
            parameters: values,
            results: cachedResults,
            result: {
              name: (calcName && calcName.trim()) ? calcName.trim() : '当前计算',
              extras: {
                project: (project && project.trim()) ? project.trim() : undefined,
                line: (line && line.trim()) ? line.trim() : undefined
              }
            }
          });
        } catch (_) {}
        try { appendOfflineHistory(values, cachedResults); } catch (_) {}
        setActiveTab('result');
        setIsCalculating(false);
        message.success(t('calc.result.using_cache'));
        return;
      }
      
      // 模拟计算过程
      setTimeout(() => {
        try {
          const results = calculateProtectionSettings(values);
          const { warnings: engWarnings, blockers } = runEngineeringChecks(values, results);
          if (Array.isArray(blockers) && blockers.length > 0) {
            blockers.forEach(b => message.error(b));
            setIsCalculating(false);
            return;
          }
          
          // 缓存计算结果
          cacheResult(values, results);
          
          setCalculationResult({
            parameters: values,
            results,
            protectionTypeName: protectionTypes.find(t => t.value === values.protectionType)?.label,
            engineeringWarnings: [...(inputWarnings || []), ...(engWarnings || []), ...(tplWarnings || [])],
            deviceWarnings: deviceWarnings || [],
            deviceBlockers: deviceBlockers || []
          });
          try {
            setCurrentCalculationCache({
              id: 'local_current',
              parameters: values,
              results,
              result: {
                name: (calcName && calcName.trim()) ? calcName.trim() : '当前计算',
                extras: {
                  project: (project && project.trim()) ? project.trim() : undefined,
                  line: (line && line.trim()) ? line.trim() : undefined
                }
              }
            });
          } catch (_) {}
          
          try { appendOfflineHistory(values, results); } catch (_) {}
          setActiveTab('result');
          setIsCalculating(false);
          message.success(t('calc.result.done'));
        } catch (error) {
          message.error(t('calc.result.error'));
          setIsCalculating(false);
        }
      }, 1000);
    });
  };

  // 未登录态：自动将成功计算写入本地历史缓存（节流 1s）
  const appendOfflineHistory = (params, results) => {
    try {
      if (userId) return; // 仅未登录写入本地历史
      const now = Date.now();
      if (now - (lastOfflineWriteRef.current || 0) < 1000) return; // 节流
      lastOfflineWriteRef.current = now;
      const name = (calcName && calcName.trim()) ? calcName.trim() : '当前计算';
      // 构建extras（含叠加与选择性快照）
      let extras = {
        project: (project && project.trim()) ? project.trim() : undefined,
        line: (line && line.trim()) ? line.trim() : undefined
      };
      try {
        const snapshot = collectOverlayAndSelectivityExtras();
        extras = { ...extras, ...snapshot };
      } catch (_) {}
      try {
        if (wizardEvents && wizardEvents.length) {
          extras.autoTuningLogEvents = wizardEvents;
          const paramSummary = buildParamChangeSummary(wizardEvents || []);
          if (paramSummary) extras.autoTuningParamSummary = paramSummary;
        }
      } catch (_) {}
      const record = {
        id: `local_${now}`,
        user_id: 'anon',
        created_at: new Date(now).toISOString(),
        parameters: params,
        results,
        result: {
          name,
          remark: (remark || '').trim(),
          extras
        }
      };
      const prev = getHistoryCache();
      const next = [record, ...prev].slice(0, 50);
      setHistoryCache(next);
      setHistoryCandidates(next);
    } catch (_) {}
  };

  // 结果摘要（用于历史列表展示）
  const summarizeResult = (type, results) => {
    try {
      switch (type) {
        case 'overcurrent':
          return { 
            value: results?.pickupCurrent, 
            unit: 'A',
            extras: {
              secondaryCurrent: results?.secondaryCurrent,
              sensitivity: results?.sensitivity,
              timeMultiplier: results?.timeMultiplier,
              curveType: results?.curveType,
              operateTime: results?.operateTime
            }
          };
        case 'distance':
          return { 
            value: results?.zone1Setting, 
            unit: 'Ω',
            extras: {
              secondaryImpedance: results?.secondaryImpedance,
              zone2Setting: results?.zone2Setting,
              zone3Setting: results?.zone3Setting,
              reach: results?.reach
            }
          };
        case 'differential':
          return { 
            value: results?.diffInstantaneousSecondary, 
            unit: 'A',
            extras: {
              differentialCurrentSetting: results?.differentialCurrentSetting,
              restraintSlope: results?.restraintSlope,
              secondHarmonicBlocking: results?.secondHarmonicBlocking
            }
          };
        case 'directional':
          return {
            value: results?.direction,
            unit: '',
            extras: {
              secondaryVoltage: results?.secondaryVoltage,
              secondaryCurrent: results?.secondaryCurrent,
              pickupCurrent: results?.pickupCurrent,
              sensitivity: results?.sensitivity,
              polarizingAngle: results?.polarizingAngle
            }
          };
        case 'zero_sequence':
          return {
            value: results?.zeroSequenceCurrent,
            unit: 'A',
            extras: {
              pickupCurrent: results?.pickupCurrent,
              sensitivity: results?.sensitivity
            }
          };
        case 'longitudinal':
          return {
            value: results?.differentialCurrent,
            unit: 'A',
            extras: {
              pickupCurrent: results?.pickupCurrent,
              operateTime: results?.operateTime
            }
          };
        default:
          return { value: null, unit: '' };
      }
    } catch (_) {
      return { value: null, unit: '' };
    }
  };

  // 保存结果到 Supabase
  const handleSaveResult = async () => {
    try {
      if (!calculationResult) {
        message.warning(t('calc.save.need_calculation'));
        return;
      }
      if (!userId) {
        message.warning(t('calc.save.need_login'));
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning(t('calc.save.need_name'));
        return;
      }
      const summary = summarizeResult(
        calculationResult.parameters?.protectionType,
        calculationResult.results
      );
      const versionStamp = generateVersionStamp();
      const feederId = form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      const deviceRefs = f ? { feeder_id: feederId, ct_id: f.ct_id, pt_id: f.pt_id, relay_id: f.relay_id } : (feederId ? { feeder_id: feederId } : undefined);
      const parametersWithRefs = deviceRefs ? { ...calculationResult.parameters, deviceRefs } : calculationResult.parameters;
      const resultWithExtras = { 
        ...summary, 
        name: calcName.trim(), 
        remark: remark || undefined, 
        // 嵌入完整计算结果，便于后续叠加列表识别 axesType
        results: calculationResult.results,
        extras: { 
          versionStamp, 
          deviceRefs, 
          project: (project && project.trim()) ? project.trim() : undefined, 
          line: (line && line.trim()) ? line.trim() : undefined 
        } 
      };
      // 注入叠加视图与选择性面板/矩阵快照
      try {
        const snapshot = collectOverlayAndSelectivityExtras();
        resultWithExtras.extras = { ...resultWithExtras.extras, ...snapshot };
      } catch (_) {}
      // 注入自动整定变更日志事件
      try {
        if (wizardEvents && wizardEvents.length) {
          resultWithExtras.extras = { ...resultWithExtras.extras, autoTuningLogEvents: wizardEvents };
          const paramSummary = buildParamChangeSummary(wizardEvents || []);
          if (paramSummary) {
            resultWithExtras.extras = { ...resultWithExtras.extras, autoTuningParamSummary: paramSummary };
          }
        }
      } catch (_) {}
      const payload = {
        user_id: userId,
        calculation_type: calculationResult.parameters?.protectionType,
        parameters: parametersWithRefs,
        result: resultWithExtras
      };
      // 如果离线，直接入队并提示（优先更新）
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const patch = {
          parameters: parametersWithRefs,
          result: resultWithExtras
        };
        if (currentRecordId) {
          enqueue('update_meta', { id: currentRecordId, patch });
          message.success('离线已入队更新');
        } else {
          enqueue('save_calculation', payload);
          message.success(t('calc.save.offline_enqueued'));
        }
        return;
      }

      // 在线：已有ID直接更新
      if (currentRecordId) {
        const { data, error } = await supabase
          .from('calculations_23813')
          .update({
            parameters: parametersWithRefs,
            result: resultWithExtras
          })
          .eq('id', currentRecordId)
          .select()
          .single();
        if (error) {
          message.error(t('calc.save.exception_prefix') + (error.message || '更新失败'));
          return;
        }
        message.success('保存成功（已更新）');
        return;
      }

      // 在线：尝试按名称+参数+类型去重，命中则更新
      try {
        const { data: existingList, error: qerr } = await supabase
          .from('calculations_23813')
          .select('id, result, parameters, calculation_type')
          .eq('user_id', userId)
          .eq('calculation_type', calculationResult.parameters?.protectionType)
          .order('created_at', { ascending: false })
          .limit(50);
        if (!qerr && Array.isArray(existingList)) {
          const candidate = existingList.find(r => (
            (r?.result?.name || '').trim() === calcName.trim() &&
            JSON.stringify(r?.parameters || {}) === JSON.stringify(parametersWithRefs || {})
          ));
          if (candidate?.id) {
            const { error: uerr } = await supabase
              .from('calculations_23813')
              .update({
                parameters: parametersWithRefs,
                result: resultWithExtras
              })
              .eq('id', candidate.id)
              .select()
              .single();
            if (uerr) {
              message.error(t('calc.save.exception_prefix') + (uerr.message || '更新失败'));
              return;
            }
            setCurrentRecordId(candidate.id);
            message.success('保存成功（已更新）');
            return;
          }
        }
      } catch (_) {
        // 查询异常时走插入
      }

      // 插入并获取ID，后续重复保存将更新此记录
      const { data, error } = await supabase
        .from('calculations_23813')
        .insert([payload])
        .select()
        .single();
      if (error) {
        // 网络/权限等失败，尝试入队
        enqueue('save_calculation', payload);
        message.warning(t('calc.save.failure_enqueued'));
        return;
      }
      setCurrentRecordId(data?.id || null);
      message.success(t('calc.save.success'));
    } catch (error) {
      message.error(t('calc.save.exception_prefix') + (error.message || '未知错误'));
    }
  };

  // 导出报告（Markdown）
  const handleExportReport = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可导出的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const prefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
      // 设备引用与详情（用于报告“设备来源”部分）
      const feederId = form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      const deviceRefs = f ? { feeder_id: feederId, ct_id: f.ct_id, pt_id: f.pt_id, relay_id: f.relay_id } : (feederId ? { feeder_id: feederId } : undefined);
      const deviceDetails = f ? {
        feeder: { id: f.id, name: f.name || f.label || f.id },
        bus: { voltage_kV: f.bus?.voltage_kV },
        ratedCurrent: f.ratedCurrent,
        ctRatio: f.ctRatio,
        source: f.source,
        transformer: f.transformer,
        line: f.line,
        ground: f.ground
      } : undefined;
      // 尝试捕获曲线图截图以嵌入 Markdown
      let chartImageDataUrl = null;
      try {
        const chartEl = document.querySelector('.result-section .h-curve-svg') || document.querySelector('.result-section svg');
        if (chartEl) chartImageDataUrl = await captureElementToPngDataUrl(chartEl);
      } catch (_) {}
      const versionStamp = generateVersionStamp();
      // 注入选择性矩阵（若叠加曲线可用）
      let selectivityMatrix = null;
      try {
        const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
        const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
        const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
          ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
          : visible;
        if (seriesForMatrix.length >= 2) {
          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
          selectivityMatrix = { ...matrix, minMargin: mmForMatrix };
        }
      } catch (_) {}
      const { md, filename } = buildMarkdownReport({
        parameters: calculationResult.parameters,
        results: calculationResult.results,
        remark,
        name: calcName.trim(),
        extras: {
          chartImageDataUrl,
          versionStamp,
          project: (project || '').trim(),
          line: (line || '').trim(),
          sortText: sortTextLocal,
          filenamePrefixResolved: prefixResolved,
          deviceRefs,
          deviceDetails,
          includeParamNotes: exportIncludeParamNotes,
          includeDeviceSource: exportIncludeDeviceSource,
          includeSelectivity: exportIncludeSelectivity,
          selectivityMatrix,
          fileNamePrefix: exportFileNamePrefix,
          engineeringWarnings: Array.isArray(calculationResult.engineeringWarnings) ? calculationResult.engineeringWarnings : [],
          deviceWarnings: Array.isArray(calculationResult.deviceWarnings) ? calculationResult.deviceWarnings : [],
          deviceBlockers: Array.isArray(calculationResult.deviceBlockers) ? calculationResult.deviceBlockers : [],
          autoTuningLogEvents: (wizardEvents && wizardEvents.length) ? wizardEvents : undefined
        }
      });
      const finalBase = buildFinalBaseName((calcName || '').trim(), prefixResolved);
      downloadTextFile(md, `${finalBase}.md`, 'text/markdown;charset=utf-8');
      message.success('报告导出成功');
    } catch (error) {
      message.error('导出失败: ' + (error.message || '未知错误'));
    }
  };

  // 导出为 PDF（截图结果区域）
  const handleExportPDF = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可导出的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      setIsExportingPdf(true);
      await new Promise((r) => setTimeout(r, 50));
      const element = resultRef.current || document.querySelector('.result-section');
      if (!element) {
        message.error('未找到结果区域，无法导出');
        setIsExportingPdf(false);
        return;
      }
      const type = calculationResult.parameters?.protectionType || 'unknown';
      const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const prefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
      const finalBase = buildFinalBaseName((calcName || '').trim(), prefixResolved);
      await exportElementToPdf({ element, name: finalBase, type, appendTimestamp: false, t });
      message.success('PDF 导出成功');
    } catch (error) {
      message.error('PDF 导出失败: ' + (error.message || '未知错误'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  // 导出为 PDF（使用统一模板组件）
  const handleExportPDFReport = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可导出的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      setIsExportingPdf(true);
      const parameters = calculationResult.parameters || {};
      const results = calculationResult.results || {};
      const type = parameters?.protectionType || 'unknown';
      const name = calcName.trim();
      // 设备引用与详情（用于报告“设备来源”部分）
      const feederId = form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      const deviceRefs = f ? { feeder_id: feederId, ct_id: f.ct_id, pt_id: f.pt_id, relay_id: f.relay_id } : (feederId ? { feeder_id: feederId } : undefined);
      const deviceDetails = f ? {
        feeder: { id: f.id, name: f.name || f.label || f.id },
        bus: { voltage_kV: f.bus?.voltage_kV },
        ratedCurrent: f.ratedCurrent,
        ctRatio: f.ctRatio,
        source: f.source,
        transformer: f.transformer,
        line: f.line,
        ground: f.ground
      } : undefined;
      const extras = { 
        versionStamp: generateVersionStamp(), 
        engineeringWarnings: Array.isArray(calculationResult.engineeringWarnings) ? calculationResult.engineeringWarnings : [],
        deviceWarnings: Array.isArray(calculationResult.deviceWarnings) ? calculationResult.deviceWarnings : [],
        deviceBlockers: Array.isArray(calculationResult.deviceBlockers) ? calculationResult.deviceBlockers : [],
        project: (project || '').trim(),
        line: (line || '').trim(),
        deviceRefs,
        deviceDetails,
        includeParamNotes: exportIncludeParamNotes,
        includeDeviceSource: exportIncludeDeviceSource,
        includeSelectivity: exportIncludeSelectivity,
        autoTuningLogEvents: (wizardEvents && wizardEvents.length) ? wizardEvents : undefined
      };
      try {
        const paramSummary = buildParamChangeSummary(wizardEvents || []);
        if (paramSummary) extras.autoTuningParamSummary = paramSummary;
      } catch (_) {}
      // 注入叠加视图快照（含解析后的前缀）
      try {
        const overlayDefs = (overlaySeries || []).map(s => ({
          key: s.key || s.label,
          label: s.label,
          color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color),
          pickup: s.pickup,
          TMS: s.TMS,
          curveType: s.curveType,
          axesType: s.axesType,
          zones: s.meta?.zones,
          characteristic: s.meta?.characteristic
        }));
        if (overlayDefs.length > 0) {
          const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
          const filenamePrefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
          extras.overlay = {
            defs: overlayDefs,
            settings: {
              showLegend: overlayShowLegend,
              zoomX: overlayZoomX,
              zoomY: overlayZoomY,
              panX: overlayPanX,
              panY: overlayPanY,
              showTooltips: overlayTooltipsEnabled,
              enableBoxZoom: overlayBoxZoomEnabled,
              colorBlindMode: overlayColorBlindMode,
              refLineKeys: overlayRefLineKeys
            },
            colorMap: overlayColorMap,
            hiddenLabels: overlayHiddenLabels,
            minMargin: overlayMinMargin,
            minMargins: overlayMinMargins,
            filenamePrefixResolved
          };
        }
      } catch (_) {}
      if (selPanelEnabled) {
        extras.selectivityPanel = {
          enabled: true,
          seriesOrder: selPanelOrder,
          globalMinMargin: selPanelGlobalMinMargin,
          minMargins: selPanelMinMargins,
        };
      }
      // 注入选择性矩阵（若叠加曲线可用）
      try {
        const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
        const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
        const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
          ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
          : visible;
        if (seriesForMatrix.length >= 2) {
          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
          extras.selectivityMatrix = { ...matrix, minMargin: mmForMatrix };
        }
      } catch (_) {}
  
      // 临时容器渲染导出内容
      const container = document.createElement('div');
      container.className = 'print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport
          name={name}
          type={type}
          parameters={parameters}
          results={results}
          remark={remark}
          extras={extras}
        />
      );
      await new Promise((r) => setTimeout(r, 50));
      const sortTextLocal2 = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const prefixResolved2 = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal2 });
      const nameWithPrefix2 = (prefixResolved2 && prefixResolved2.trim()) ? `${prefixResolved2.trim()}_${name}` : name;
      await exportElementToPdf({ element: container, name: nameWithPrefix2, type, title: '计算页计算报告', watermark: 'JDBH', appendTimestamp: false, t });
      root.unmount();
      document.body.removeChild(container);
      message.success('PDF 导出成功');
    } catch (error) {
      message.error('PDF 导出失败: ' + (error.message || '未知错误'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  // 导出为 HTML（使用统一模板组件）
  const handleExportHtmlReport = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可导出的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      const parameters = calculationResult.parameters || {};
      const results = calculationResult.results || {};
      const type = parameters?.protectionType || 'unknown';
      const name = calcName.trim();
      // 设备引用与详情（用于报告“设备来源”部分）
      const feederId = form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      const deviceRefs = f ? { feeder_id: feederId, ct_id: f.ct_id, pt_id: f.pt_id, relay_id: f.relay_id } : (feederId ? { feeder_id: feederId } : undefined);
      const deviceDetails = f ? {
        feeder: { id: f.id, name: f.name || f.label || f.id },
        bus: { voltage_kV: f.bus?.voltage_kV },
        ratedCurrent: f.ratedCurrent,
        ctRatio: f.ctRatio,
        source: f.source,
        transformer: f.transformer,
        line: f.line,
        ground: f.ground
      } : undefined;
      const extras = {
        versionStamp: generateVersionStamp(),
        engineeringWarnings: Array.isArray(calculationResult.engineeringWarnings) ? calculationResult.engineeringWarnings : [],
        deviceWarnings: Array.isArray(calculationResult.deviceWarnings) ? calculationResult.deviceWarnings : [],
        deviceBlockers: Array.isArray(calculationResult.deviceBlockers) ? calculationResult.deviceBlockers : [],
        project: (project || '').trim(),
        line: (line || '').trim(),
        deviceRefs,
        deviceDetails,
        includeParamNotes: exportIncludeParamNotes,
        includeDeviceSource: exportIncludeDeviceSource,
        includeSelectivity: exportIncludeSelectivity,
        autoTuningLogEvents: (wizardEvents && wizardEvents.length) ? wizardEvents : undefined
      };
      try {
        const paramSummary2 = buildParamChangeSummary(wizardEvents || []);
        if (paramSummary2) extras.autoTuningParamSummary = paramSummary2;
      } catch (_) {}
      // 注入叠加视图快照（含解析后的前缀）
      try {
        const overlayDefs = (overlaySeries || []).map(s => ({
          key: s.key || s.label,
          label: s.label,
          color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color),
          pickup: s.pickup,
          TMS: s.TMS,
          curveType: s.curveType,
          axesType: s.axesType,
          zones: s.meta?.zones,
          characteristic: s.meta?.characteristic
        }));
        if (overlayDefs.length > 0) {
          const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
          const filenamePrefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
          extras.overlay = {
            defs: overlayDefs,
            settings: {
              showLegend: overlayShowLegend,
              zoomX: overlayZoomX,
              zoomY: overlayZoomY,
              panX: overlayPanX,
              panY: overlayPanY,
              showTooltips: overlayTooltipsEnabled,
              enableBoxZoom: overlayBoxZoomEnabled,
              colorBlindMode: overlayColorBlindMode,
              refLineKeys: overlayRefLineKeys
            },
            colorMap: overlayColorMap,
            hiddenLabels: overlayHiddenLabels,
            minMargin: overlayMinMargin,
            minMargins: overlayMinMargins,
            filenamePrefixResolved
          };
        }
      } catch (_) {}
      if (selPanelEnabled) {
        extras.selectivityPanel = {
          enabled: true,
          seriesOrder: selPanelOrder,
          globalMinMargin: selPanelGlobalMinMargin,
          minMargins: selPanelMinMargins,
        };
      }
      // 注入选择性矩阵（若叠加曲线可用）
      try {
        const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
        const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
        const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
          ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
          : visible;
        if (seriesForMatrix.length >= 2) {
          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
          extras.selectivityMatrix = { ...matrix, minMargin: mmForMatrix };
        }
      } catch (_) {}
      const container = document.createElement('div');
      container.className = 'print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport
          name={name}
          type={type}
          parameters={parameters}
          results={results}
          remark={remark}
          extras={extras}
        />
      );
      await new Promise((r) => setTimeout(r, 50));
      const sortTextLocal3 = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const prefixResolved3 = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal3 });
      const finalBase3 = buildFinalBaseName(name, prefixResolved3);
      exportElementToHtml({ element: container, name: finalBase3, title: '计算页计算报告', appendTimestamp: false });
      root.unmount();
      document.body.removeChild(container);
      message.success('HTML 导出成功');
    } catch (error) {
      message.error('HTML 导出失败: ' + (error.message || '未知错误'));
    }
  };

  // 导出为 Word（使用统一模板组件）
  const handleExportWordReport = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可导出的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      const parameters = calculationResult.parameters || {};
      const results = calculationResult.results || {};
      const type = parameters?.protectionType || 'unknown';
      const name = calcName.trim();
      // 设备引用与详情（用于报告“设备来源”部分）
      const feederId = form.getFieldValue('selectedFeederId');
      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
      const deviceRefs = f ? { feeder_id: feederId, ct_id: f.ct_id, pt_id: f.pt_id, relay_id: f.relay_id } : (feederId ? { feeder_id: feederId } : undefined);
      const deviceDetails = f ? {
        feeder: { id: f.id, name: f.name || f.label || f.id },
        bus: { voltage_kV: f.bus?.voltage_kV },
        ratedCurrent: f.ratedCurrent,
        ctRatio: f.ctRatio,
        source: f.source,
        transformer: f.transformer,
        line: f.line,
        ground: f.ground
      } : undefined;
      const extras = {
        versionStamp: generateVersionStamp(),
        engineeringWarnings: Array.isArray(calculationResult.engineeringWarnings) ? calculationResult.engineeringWarnings : [],
        deviceWarnings: Array.isArray(calculationResult.deviceWarnings) ? calculationResult.deviceWarnings : [],
        deviceBlockers: Array.isArray(calculationResult.deviceBlockers) ? calculationResult.deviceBlockers : [],
        project: (project || '').trim(),
        line: (line || '').trim(),
        deviceRefs,
        deviceDetails,
        includeParamNotes: exportIncludeParamNotes,
        includeDeviceSource: exportIncludeDeviceSource,
        includeSelectivity: exportIncludeSelectivity
      };
      try {
        const paramSummary3 = buildParamChangeSummary(wizardEvents || []);
        if (paramSummary3) extras.autoTuningParamSummary = paramSummary3;
      } catch (_) {}
      // 注入叠加视图快照（含解析后的前缀）
      try {
        const overlayDefs = (overlaySeries || []).map(s => ({
          key: s.key || s.label,
          label: s.label,
          color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color),
          pickup: s.pickup,
          TMS: s.TMS,
          curveType: s.curveType,
          axesType: s.axesType,
          zones: s.meta?.zones,
          characteristic: s.meta?.characteristic
        }));
        if (overlayDefs.length > 0) {
          const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
          const filenamePrefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
          extras.overlay = {
            defs: overlayDefs,
            settings: {
              showLegend: overlayShowLegend,
              zoomX: overlayZoomX,
              zoomY: overlayZoomY,
              panX: overlayPanX,
              panY: overlayPanY,
              showTooltips: overlayTooltipsEnabled,
              enableBoxZoom: overlayBoxZoomEnabled,
              colorBlindMode: overlayColorBlindMode,
              refLineKeys: overlayRefLineKeys
            },
            colorMap: overlayColorMap,
            hiddenLabels: overlayHiddenLabels,
            minMargin: overlayMinMargin,
            minMargins: overlayMinMargins,
            filenamePrefixResolved
          };
        }
      } catch (_) {}
      if (selPanelEnabled) {
        extras.selectivityPanel = {
          enabled: true,
          seriesOrder: selPanelOrder,
          globalMinMargin: selPanelGlobalMinMargin,
          minMargins: selPanelMinMargins,
        };
      }
      // 注入选择性矩阵（若叠加曲线可用）
      try {
        const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
        const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
        const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
          ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
          : visible;
        if (seriesForMatrix.length >= 2) {
          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
          extras.selectivityMatrix = { ...matrix, minMargin: mmForMatrix };
        }
      } catch (_) {}
      const container = document.createElement('div');
      container.className = 'print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport
          name={name}
          type={type}
          parameters={parameters}
          results={results}
          remark={remark}
          extras={extras}
        />
      );
      await new Promise((r) => setTimeout(r, 50));
      const sortTextLocal4 = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const prefixResolved4 = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal4 });
      const finalBase4 = buildFinalBaseName(name, prefixResolved4);
      exportElementToWord({ element: container, name: finalBase4, title: '计算页计算报告', appendTimestamp: false });
      root.unmount();
      document.body.removeChild(container);
      message.success('Word 导出成功');
    } catch (error) {
      message.error('Word 导出失败: ' + (error.message || '未知错误'));
    }
  };

  // 生成只读分享链接（一次性 Token）
  const handleGenerateShareLink = async () => {
    try {
      if (!calculationResult) {
        message.warning('暂无可分享的结果');
        return;
      }
      if (!calcName || !calcName.trim()) {
        message.warning('请填写名称');
        return;
      }
      const parameters = calculationResult.parameters || {};
      const results = calculationResult.results || {};
      const type = parameters?.protectionType || 'unknown';
      // 收集叠加视图状态（只读快照）
      const overlayDefs = (overlaySeries || []).map(s => ({
        key: s.key || s.label,
        label: s.label,
        color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color),
        pickup: s.pickup,
        TMS: s.TMS,
        curveType: s.curveType,
        axesType: s.axesType,
        // 携带多坐标系的结构化信息，便于分享视图恢复
        zones: s.meta?.zones,
        characteristic: s.meta?.characteristic
      }));
      const sortTextLocal = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
      const filenamePrefixResolved = resolveFilenamePrefix('', { project: (project || '').trim(), line: (line || '').trim(), sort: sortTextLocal });
      const overlayPayload = overlayDefs.length > 0 ? {
        defs: overlayDefs,
        settings: {
          showLegend: overlayShowLegend,
          zoomX: overlayZoomX,
          zoomY: overlayZoomY,
          panX: overlayPanX,
          panY: overlayPanY,
          showTooltips: overlayTooltipsEnabled,
          enableBoxZoom: overlayBoxZoomEnabled,
          colorBlindMode: overlayColorBlindMode,
          refLineKeys: overlayRefLineKeys
        },
        colorMap: overlayColorMap,
        hiddenLabels: overlayHiddenLabels,
        minMargin: overlayMinMargin,
        minMargins: overlayMinMargins,
        filenamePrefixResolved
      } : undefined;
      // 选择性面板配置（可选）
      const selectivityPanelPayload = selPanelEnabled ? {
        enabled: true,
        seriesOrder: selPanelOrder,
        globalMinMargin: selPanelGlobalMinMargin,
        minMargins: selPanelMinMargins
      } : undefined;
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 24h 有效期
      const payload = {
        name: calcName.trim(),
        type,
        parameters,
        results,
        remark,
        extras: { 
          versionStamp: generateVersionStamp(), 
          overlay: overlayPayload,
          selectivityPanel: selectivityPanelPayload,
          // 注入选择性矩阵（若叠加曲线可用），便于 ShareView 端直接展示
          ...(function () {
            try {
              const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
              const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
              const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
                ? selPanelOrder.map(k => colored.find(s => (s.key || s.label) === k)).filter(Boolean)
                : visible;
              if (seriesForMatrix.length >= 2) {
                const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
                const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
                return { selectivityMatrix: { ...matrix, minMargin: mmForMatrix } };
              }
            } catch (_) {}
            return {};
          })(),
          expiresAt,
          project: (project || '').trim(),
          line: (line || '').trim()
        }
      };
      const token = generateRandomToken(24);
      const url = await buildShareUrlPreferShort({
        payload,
        token,
        expiresAt,
        policy: { requiresLogin: shareRequiresLogin, maxUses: shareMaxUses },
        preferShort: sharePreferShort
      });
      await navigator.clipboard.writeText(url).catch(() => {});
      window.open(url, '_blank');
      message.success(t('share.copy.success'));
    } catch (error) {
      message.error(t('share.generate.failure_prefix') + (error.message || '未知错误'));
    }
  };

  const handleProtectionChange = (value) => {
    setSelectedProtection(value);
    // 选中并应用默认模板
    const defaultTplKey = DEFAULT_TEMPLATE_FOR_TYPE[value];
    const presetsByType = PARAM_TEMPLATES[value] || {};
    const preset = defaultTplKey ? presetsByType[defaultTplKey] : null;
    // 同步保护类型与默认模板选择
    form.setFieldsValue({ protectionType: value, paramTemplate: defaultTplKey });
    // 填充默认模板参数
      if (preset) {
        form.setFieldsValue(preset);
        try {
          const { errors, warnings } = validateTemplateBounds(preset);
          (warnings || []).filter(w => w !== '时间倍数 TMS 建议处于 0.05~1.5').forEach(w => message.warning(w));
          (errors || []).forEach(e => message.error(e));
        } catch (_) {}
        // 应用默认模板后即时提示关键字段（不弹出成功提示）
        notifyKeyFieldHints();
      }
  };

  return (
    <div className="calculation-page">
      <div className="page-header">
        <Title level={3}>
          <CalculatorOutlined /> 继电保护定值计算
        </Title>
        <Text type="secondary">基于IEC 60255标准的专业计算工具</Text>
      </div>

      <Card className="main-card">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: 'input',
              label: '参数输入',
              children: (
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={16}>
                    <Card title="系统参数" className="input-section">
                      <Form
                        form={form}
                        layout="vertical"
                        onValuesChange={(changedValues) => {
                          if (changedValues.protectionType) {
                            setSelectedProtection(changedValues.protectionType);
                          }
                          // 距离保护：单位切换与长度联动，自动计算总阻抗
                          if (selectedProtection === 'distance') {
                            const mode = form.getFieldValue('lineImpedanceUnitMode');
                            const values = form.getFieldsValue([ 'lineLength_km', 'lineR_per_km_ohm', 'lineX_per_km_ohm' ]);
                            const newVals = computeTotalsFromPerKm({ mode, ...values });
                            if (Object.keys(newVals).length > 0) {
                              form.setFieldsValue(newVals);
                            }
                          }
                        }}
                      >
                        {/** 在表单上下文内监听关键字段的变化并同步到外部状态 */}
                        {(() => {
                          const SCWatcher = ({ onChange }) => {
                            const fid = Form.useWatch('selectedFeederId');
                            const mp = Form.useWatch('motorPercent');
                            const gt = Form.useWatch('groundingType');
                            useEffect(() => {
                              onChange({ selectedFeederId: fid, motorPercent: mp, groundingType: gt });
                            }, [fid, mp, gt]);
                            return null;
                          };
                          return <SCWatcher onChange={setScInputs} />;
                        })()}
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="系统电压 (kV)"
                              name="systemVoltage"
                              rules={getFieldRules(selectedProtection, 'systemVoltage')}
                              extra={renderFieldRangeHint(selectedProtection, 'systemVoltage')}
                            >
                              <InputNumber 
                                style={{ width: '100%' }} 
                                placeholder="请输入系统电压" 
                                min={0}
                                step={0.1}
                                formatter={(v) => (v === undefined || v === null ? '' : `${v} kV`)}
                                parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                              data-testid="systemVoltage" />
                            </Form.Item>
                          </Col>
                          
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="额定电流 (A)"
                              name="ratedCurrent"
                              rules={getFieldRules(selectedProtection, 'ratedCurrent')}
                              extra={renderFieldRangeHint(selectedProtection, 'ratedCurrent')}
                            >
                              <InputNumber 
                                style={{ width: '100%' }} 
                                placeholder="请输入额定电流" 
                                min={0}
                                formatter={(v) => (v === undefined || v === null ? '' : `${v} A`)}
                                parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                              data-testid="ratedCurrent" />
                            </Form.Item>
                          </Col>
                          
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="CT变比"
                              name="ctRatio"
                              rules={getFieldRules(selectedProtection, 'ctRatio')}
                              extra={renderCombinedHint('ctRatio')}
                            >
                              <InputNumber 
                                style={{ width: '100%' }} 
                                placeholder="请输入CT变比" 
                                min={1}
                                formatter={(v) => (v === undefined || v === null ? '1:' : `1:${v}`)}
                                parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                              data-testid="ctRatio"
                              />
                            </Form.Item>
                          </Col>
                          
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="PT变比"
                              name="ptRatio"
                              rules={getFieldRules(selectedProtection, 'ptRatio')}
                              extra={renderCombinedHint('ptRatio')}
                            >
                              <InputNumber 
                                style={{ width: '100%' }} 
                                placeholder="请输入PT变比" 
                                min={1}
                                formatter={(v) => (v === undefined || v === null ? '1:' : `1:${v}`)}
                                parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                              />
                            </Form.Item>
                          </Col>
                          
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="故障电流 (A)"
                              name="faultCurrent"
                              rules={getFieldRules(selectedProtection, 'faultCurrent')}
                              extra={renderCombinedHint('faultCurrent')}
                            >
                              <InputNumber 
                                style={{ width: '100%' }} 
                                placeholder="请输入故障电流" 
                                min={0}
                              data-testid="faultCurrent" />
                            </Form.Item>
                          </Col>
                          
                          {selectedProtection === 'differential' && (
                            <>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="变压器容量 (kVA)"
                                  name="transformerCapacity"
                                  rules={getFieldRules(selectedProtection, 'transformerCapacity')}
                                  extra={renderFieldRangeHint(selectedProtection, 'transformerCapacity')}
                                >
                                  <InputNumber 
                                    style={{ width: '100%' }} 
                                    placeholder="请输入变压器容量" 
                                    min={0}
                                    formatter={(v) => (v === undefined || v === null ? '' : `${v} kVA`)}
                                    parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24}>
                                <Card size="small" style={{ marginTop: 8 }}>
                                  <Typography.Title level={5} style={{ marginBottom: 8 }}>差动特性分段 (Id–Ir)</Typography.Title>
                                  <Form.Item name="segments" valuePropName="value">
                                    <SegmentsEditor />
                                  </Form.Item>
                                  <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
                                    说明：若不设置分段，系统将默认生成两段（K=0 至差动定值；K=比率制动斜率延伸至∞）。
                                  </Typography.Paragraph>
                                </Card>
                              </Col>
                            </>
                          )}
                          
                          {['overcurrent', 'directional', 'zero_sequence', 'distance'].includes(selectedProtection) && (
                              <>
                                <Col xs={24}>
                                  <Form.Item label="从设备库选择馈线" name="selectedFeederId">
                                    <Space style={{ width: '100%' }} direction="vertical">
                                      <Select
                                        placeholder="选择馈线自动填充参数并计算短路"
                                        options={feederOptions.map(f => ({ value: (f.value ?? f.id), label: f.label }))}
                                        onChange={async (feederId) => {
                                          let f = null;
                                          try {
                                            f = await fetchFeederByIdAsync(feederId);
                                          } catch (_) {}
                                          if (!f) {
                                            f = getFeederById(feederId);
                                          }
                                          if (f) {
                                            setSelectedFeederObj(f);
                                            // 推断接地类型并基于其计算短路电流
                                            const nextGroundingType = inferGroundingTypeFromFeeder(f) || form.getFieldValue('groundingType');
                                            const sc = computeShortCircuitForFeeder(f, { motor_percent: form.getFieldValue('motorPercent') || 0, groundingType: nextGroundingType });
                                            // 预填系统与短路参数
                                            const basePatch = {
                                              selectedFeederId: feederId,
                                              systemVoltage: f.bus?.voltage_kV ?? f.bus_voltage_kV ?? form.getFieldValue('systemVoltage'),
                                              ratedCurrent: f.ratedCurrent ?? f.rated_current,
                                              ctRatio: f.ctRatio ?? f.ct_ratio,
                                              ptRatio: f.ptRatio ?? f.pt_ratio ?? form.getFieldValue('ptRatio'),
                                              groundingType: nextGroundingType,
                                              faultCurrent: Math.round(sc?.three_phase?.Ik3p_max_A || 0),
                                              motorPercent: form.getFieldValue('motorPercent') || 0
                                            };
                                            // 方向保护：自动回填极化角
                                            if (selectedProtection === 'directional') {
                                              basePatch.polarizingAngle = suggestPolarizingAngleFromFeeder(f);
                                            }
                                            form.setFieldsValue(basePatch);
                                            // 距离保护：预填线路R/X（Ω）与长度，并计算总阻抗（util）
                                            if (selectedProtection === 'distance') {
                                              const patch = applyFeederPresetToDistance(f);
                                              form.setFieldsValue(patch);
                                            }
                                            // 根据设备信息应用典型整定（曲线族与TMS），优先匹配厂家，否则按类型默认
                                            try {
                                              const relayName = String(f?.relay_name || f?.relay_model || '').toLowerCase();
                                              const vendorAbb = relayName.includes('abb');
                                              const vendorSel = relayName.includes('sel');
                                              const vendorSchneider = relayName.includes('schneider');
                                              let tplKey = DEFAULT_TEMPLATE_FOR_TYPE[selectedProtection];
                                              const presetsByType = PARAM_TEMPLATES[selectedProtection] || {};
                                              if (selectedProtection === 'overcurrent') {
                                                if (vendorAbb && presetsByType.abb_dist_ni) tplKey = 'abb_dist_ni';
                                                else if (vendorSel && presetsByType.sel_plant_vi) tplKey = 'sel_plant_vi';
                                              } else if (selectedProtection === 'directional') {
                                                if (vendorSchneider && presetsByType.schneider_dir_vi) tplKey = 'schneider_dir_vi';
                                              } else if (selectedProtection === 'zero_sequence') {
                                                if (vendorSchneider && presetsByType.schneider_zs_vi) tplKey = 'schneider_zs_vi';
                                              } else if (selectedProtection === 'distance') {
                                                // 高压线路优先使用高压距离模板
                                                const sv = form.getFieldValue('systemVoltage');
                                                if (sv && sv >= 100 && (presetsByType.hv_line_distance)) tplKey = 'hv_line_distance';
                                              }
                                              const preset = tplKey ? presetsByType[tplKey] : null;
                                              if (preset) {
                                                const patchTpl = {};
                                                if (preset.curveType) patchTpl.curveType = preset.curveType;
                                                if (preset.timeMultiplier != null) patchTpl.timeMultiplier = preset.timeMultiplier;
                                                if (Object.keys(patchTpl).length > 0) {
                                                  form.setFieldsValue(patchTpl);
                                                  const { errors, warnings } = validateTemplateBounds({ ...form.getFieldsValue(), ...patchTpl, protectionType: selectedProtection });
                                                  (warnings || []).filter(w => w !== '时间倍数 TMS 建议处于 0.05~1.5').forEach(w => message.warning(w));
                                                  (errors || []).forEach(e => message.error(e));
                                                }
                                              }
                                            } catch (_) {}
                                            // 设备库回填完成后即时提示关键字段
                                            notifyKeyFieldHints();
                                            message.success('已从设备库回填参数并计算短路(最大)');
                                          } else {
                                            message.warning('未找到馈线');
                                          }
                                        }}
                                      />
                                      <Button type="link" icon={<ToolOutlined />} onClick={() => navigate('/device-library')}>管理设备库</Button>
                                    </Space>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Form.Item label="电机贡献(%)" name="motorPercent" initialValue={0}>
                                    <InputNumber min={0} max={100} step={1} onChange={() => {
                                      const feederId = form.getFieldValue('selectedFeederId');
                                      const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
                                      if (f) {
                                        const sc = computeShortCircuitForFeeder(f, { motor_percent: form.getFieldValue('motorPercent') || 0, groundingType: form.getFieldValue('groundingType') });
                                        form.setFieldsValue({ faultCurrent: Math.round(sc?.three_phase?.Ik3p_max_A || 0) });
                                      }
                                    }} />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Form.Item label="接地类型" name="groundingType" initialValue="solid">
                                    <Select
                                      placeholder="选择接地类型模板"
                                      options={GROUNDING_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                                      onChange={() => {
                                        const feederId = form.getFieldValue('selectedFeederId');
                                        const f = selectedFeederObj || (feederId ? getFeederById(feederId) : null);
                                        if (f) {
                                          const sc = computeShortCircuitForFeeder(f, { motor_percent: form.getFieldValue('motorPercent') || 0, groundingType: form.getFieldValue('groundingType') });
                                          form.setFieldsValue({ faultCurrent: Math.round(sc?.three_phase?.Ik3p_max_A || 0) });
                                          message.success('已应用接地类型模板并重新计算短路(最大)');
                                        }
                                      }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={24}>
                                  {scResult && (
                                    <Descriptions size="small" column={1} title="短路计算结果">
                                      <Descriptions.Item label="Ik''max (A)">{Math.round(scResult?.three_phase?.Ik3p_max_A || 0)}</Descriptions.Item>
                                      <Descriptions.Item label="Ik''min (A)">{Math.round(scResult?.three_phase?.Ik3p_min_A || 0)}</Descriptions.Item>
                                      <Descriptions.Item label="Ip 峰值 (A)">{Math.round(scResult?.three_phase?.Ip_max_A || 0)}</Descriptions.Item>
                                      <Descriptions.Item label="Zth_max (Ω)">{`${(scResult?.three_phase?.Z_th_max_ohm?.R || 0).toFixed(3)} + j${(scResult?.three_phase?.Z_th_max_ohm?.X || 0).toFixed(3)}`}</Descriptions.Item>
                                      <Descriptions.Item label="Zth_min (Ω)">{`${(scResult?.three_phase?.Z_th_min_ohm?.R || 0).toFixed(3)} + j${(scResult?.three_phase?.Z_th_min_ohm?.X || 0).toFixed(3)}`}</Descriptions.Item>
                                      <Descriptions.Item label="两相短路 Ik_LL (A)">{Math.round(scResult?.two_phase_ll?.Ik_ll_max_A || 0)}</Descriptions.Item>
                                      <Descriptions.Item label="两相接地 Ik_LLG (A)">{Math.round(scResult?.two_phase_earth?.Ik_llg_max_A || 0)}</Descriptions.Item>
                                      <Descriptions.Item label="单相接地 Ik_E (A)">{Math.round(scResult?.one_phase_earth?.Ik_1phg_max_A || 0)}</Descriptions.Item>
                                    </Descriptions>
                                  )}
                                </Col>
                                <Col xs={24} md={12}>
                                <Form.Item
                                  label="反limitedCurve"
                                  name="curveType"
                                  rules={getFieldRules(selectedProtection, 'curveType')}
                                >
                                  <Select placeholder="选择曲线族" data-testid="curveType">
                                    <Select.Option value="Normal Inverse">标准反时限 (Normal Inverse)</Select.Option>
                                    <Select.Option value="Very Inverse">非常反时限 (Very Inverse)</Select.Option>
                                    <Select.Option value="Extremely Inverse">极度反时限 (Extremely Inverse)</Select.Option>
                                    <Select.Option value="Long-time Inverse">长延时反时限 (Long-time Inverse)</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="时间倍数 (TMS)"
                                  name="timeMultiplier"
                                  rules={getFieldRules(selectedProtection, 'timeMultiplier')}
                                  extra={renderCombinedHint('timeMultiplier')}
                                >
                                  <InputNumber style={{ width: '100%' }} min={0.05} step={0.05} data-testid="timeMultiplier" />
                                </Form.Item>
                              </Col>
                              {selectedProtection === 'overcurrent' && (
                                <>
                                  <Col xs={24} md={12}>
                                    <Form.Item
                                      label="最大负荷电流 (A，一次侧)"
                                      name="maxLoadCurrent"
                                      rules={getFieldRules(selectedProtection, 'maxLoadCurrent')}
                                      extra={renderFieldRangeHint(selectedProtection, 'maxLoadCurrent')}
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder="可选：用于计算负荷基准与倍数"
                                        min={0}
                                        formatter={(v) => (v === undefined || v === null ? '' : `${v} A`)}
                                        parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item
                                      label="启动倍数 (×)"
                                      name="pickupMultiplier"
                                      rules={getFieldRules(selectedProtection, 'pickupMultiplier')}
                                      extra={renderFieldRangeHint(selectedProtection, 'pickupMultiplier')}
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder="可选：默认约 1.3×"
                                        min={1}
                                        step={0.1}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <Form.Item
                                      label="瞬时倍数 (×)"
                                      name="instantaneousMultiplier"
                                      rules={getFieldRules(selectedProtection, 'instantaneousMultiplier')}
                                      extra={renderFieldRangeHint(selectedProtection, 'instantaneousMultiplier')}
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder="可选：用于计算瞬时阈值"
                                        min={1}
                                        step={0.5}
                                      />
                                    </Form.Item>
                                  </Col>
                                </>
                              )}
                              {['overcurrent','directional','zero_sequence','distance','differential'].includes(selectedProtection) && (
                                <Col xs={24}>
                                  <Space>
                                    <Button type="dashed" icon={<ToolOutlined />} onClick={openAutoWizard}>自动整定向导</Button>
                                    <Button type="link" onClick={rollbackWizard}>回滚整定建议</Button>
                                  </Space>
                                </Col>
                              )}
                            </>
                          )}
                          {selectedProtection === 'distance' && (
                            <>
                              <Col xs={24}>
                                <Form.Item label="线路阻抗单位" name="lineImpedanceUnitMode" initialValue="total">
                                  <Radio.Group onChange={() => {
                                    const mode = form.getFieldValue('lineImpedanceUnitMode');
                                    const values = form.getFieldsValue([ 'lineLength_km', 'lineR_per_km_ohm', 'lineX_per_km_ohm' ]);
                                    const newVals = computeTotalsFromPerKm({ mode, ...values });
                                    if (Object.keys(newVals).length > 0) form.setFieldsValue(newVals);
                                  }}>
                                    <Radio.Button value="per_km">Ω/km（每公里）</Radio.Button>
                                    <Radio.Button value="total">Ω（总值）</Radio.Button>
                                  </Radio.Group>
                                </Form.Item>
                              </Col>
                              {/* 每公里模式：长度与单位阻抗（随单位切换动态渲染） */}
                              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.lineImpedanceUnitMode !== cur.lineImpedanceUnitMode}>
                                {() => ((form.getFieldValue('lineImpedanceUnitMode') || 'total') === 'per_km') && (
                                  <>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        label="线路长度 (km)"
                                        name="lineLength_km"
                                        rules={getFieldRules(selectedProtection, 'lineLength_km')}
                                        extra={renderFieldRangeHint(selectedProtection, 'lineLength_km')}
                                      >
                                        <InputNumber style={{ width: '100%' }} min={0} step={0.01} 
                                          formatter={(v) => (v === undefined || v === null ? '' : `${v} km`)}
                                          parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        label="单位电阻 R (Ω/km)"
                                        name="lineR_per_km_ohm"
                                        rules={getFieldRules(selectedProtection, 'lineR_per_km_ohm')}
                                        extra={renderFieldRangeHint(selectedProtection, 'lineR_per_km_ohm')}
                                      >
                                        <InputNumber style={{ width: '100%' }} min={0} step={0.0001} />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        label="单位电抗 X (Ω/km)"
                                        name="lineX_per_km_ohm"
                                        rules={getFieldRules(selectedProtection, 'lineX_per_km_ohm')}
                                        extra={renderFieldRangeHint(selectedProtection, 'lineX_per_km_ohm')}
                                      >
                                        <InputNumber style={{ width: '100%' }} min={0} step={0.0001} />
                                      </Form.Item>
                                    </Col>
                                  </>
                                )}
                              </Form.Item>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="线路电阻 R (Ω)"
                                  name="lineR_ohm"
                                  rules={getFieldRules(selectedProtection, 'lineR_ohm')}
                                  extra={renderFieldRangeHint(selectedProtection, 'lineR_ohm')}
                                >
                                  <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="线路电抗 X (Ω)"
                                  name="lineX_ohm"
                                  rules={getFieldRules(selectedProtection, 'lineX_ohm')}
                                  extra={renderFieldRangeHint(selectedProtection, 'lineX_ohm')}
                                >
                                  <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  label="线路阻抗角 (°)"
                                  name="lineAngleDeg"
                                  tooltip="可选，未填则根据R/X或馈线自动推算"
                                  rules={getFieldRules(selectedProtection, 'lineAngleDeg')}
                                  extra={renderFieldRangeHint(selectedProtection, 'lineAngleDeg')}
                                >
                                  <InputNumber style={{ width: '100%' }} min={0} max={90} step={0.1}
                                    formatter={(v) => (v === undefined || v === null ? '' : `${v} °`)}
                                    parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                                  />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                          {selectedProtection === 'directional' && (
                            <Col xs={24} md={12}>
                              <Form.Item
                              label="极化角 (°)"
                              name="polarizingAngle"
                              rules={getFieldRules(selectedProtection, 'polarizingAngle')}
                              extra={renderFieldRangeHint(selectedProtection, 'polarizingAngle')}
                            >
                                <InputNumber style={{ width: '100%' }} min={0} max={90} step={1} 
                                  formatter={(v) => (v === undefined || v === null ? '' : `${v} °`)}
                                  parser={(v) => (v ? parseFloat(String(v).replace(/[^\d.]/g, '')) : undefined)}
                                />
                              </Form.Item>
                            </Col>
                          )}
                          {selectedProtection === 'zero_sequence' && (
                            <Col xs={24} md={12}>
                              <Form.Item
                              label="零序系数 k0"
                              name="k0"
                              rules={getFieldRules(selectedProtection, 'k0')}
                              extra={renderFieldRangeHint(selectedProtection, 'k0')}
                            >
                                <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} />
                              </Form.Item>
                            </Col>
                          )}
                          
                          <Col xs={24} md={12}>
                             <Form.Item
                               label="保护类型"
                               name="protectionType"
                               rules={[{ required: true, message: '请选择保护类型' }]}
                             >
                               <Select 
                                 placeholder="请选择保护类型"
                                 onChange={handleProtectionChange}
                                 data-testid="protectionType"
                               >
                                 {protectionTypes.map(type => (
                                   <Select.Option key={type.value} value={type.value}>
                                     <Space>
                                       {type.icon}
                                       {type.label}
                                     </Space>
                                   </Select.Option>
                                 ))}
                               </Select>
                             </Form.Item>
                           </Col>
     
                            {/* 参数模板选择（应用预设值，模板绑定类型） */}
                           <Col xs={24} md={12}>
                             <Form.Item label="参数模板" name="paramTemplate">
                               <Select 
                                 key={selectedProtection}
                                 placeholder="选择常用工况模板"
                                 onChange={(tpl) => {
                                   const presetsByType = PARAM_TEMPLATES[selectedProtection] || {};
                                   const preset = presetsByType[tpl];
                                   if (preset) {
                                     // 先同步保护类型（模板绑定类型）
                                     if (preset.protectionType && preset.protectionType !== selectedProtection) {
                                       handleProtectionChange(preset.protectionType);
                                     }
                                     // 再填充字段
                                     form.setFieldsValue(preset);
                                     try {
                                       const { errors, warnings } = validateTemplateBounds(preset);
                                       (warnings || []).filter(w => w !== '时间倍数 TMS 建议处于 0.05~1.5').forEach(w => message.warning(w));
                                       (errors || []).forEach(e => message.error(e));
                                     } catch (_) {}
                                     // 选择模板后即时提示关键字段
                                     notifyKeyFieldHints();
                                   }
                                 }}
                               >
                                 {(TEMPLATE_OPTIONS[selectedProtection] || []).map((opt) => (
                                   <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                 ))}
                               </Select>
                             </Form.Item>
                           </Col>
                        </Row>
                        
                        <Form.Item>
                          <Button 
                            type="primary" 
                            onClick={handleCalculate} 
                            loading={isCalculating}
                            size="large"
                            block
                            data-testid="startCalculate"
                          >
                            {isCalculating ? '计算中...' : '开始计算'}
                          </Button>
                        </Form.Item>
                      </Form>
                    </Card>
                  </Col>
                  
                  <Col xs={24} lg={8}>
                    <Card title="保护类型说明" className="info-section">
                      <div className="protection-info">
                        {protectionTypes.map(type => (
                          <div 
                            key={type.value}
                            className={`protection-type ${selectedProtection === type.value ? 'selected' : ''}`}
                            onClick={() => handleProtectionChange(type.value)}
                          >
                            <Space>
                              <span className="icon">{type.icon}</span>
                              <div>
                                <Text strong>{type.label}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {type.description}
                                </Text>
                              </div>
                            </Space>
                          </div>
                        ))}
                      </div>
                      
                      <Divider />
                      
                      <div className="calculation-tips">
                        <Title level={5}>
                          <InfoCircleOutlined /> 计算提示
                        </Title>
                        <Paragraph>
                          <ul>
                            <li>请确保输入参数准确无误</li>
                            <li>计算结果仅供参考，实际应用需校核</li>
                            <li>建议结合系统运行方式调整定值</li>
                          </ul>
                        </Paragraph>
                      </div>
                    </Card>
                  </Col>
                </Row>
              )
            },
            {
              key: 'result',
              label: '计算结果',
              disabled: !calculationResult,
              children: (
                calculationResult && (
                  <div className={`result-section ${isExportingPdf ? 'print-layout' : ''}`} ref={resultRef}>
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <div className="export-summary">
                          <Text type="secondary">名称：{calcName || '未命名'}</Text>
                        </div>
                      </Col>
                      <Col xs={24} lg={16}>
                        <Card title="计算结果详情" className="result-details">
                          <Descriptions 
                            title="输入参数回顾" 
                            column={2} 
                            bordered
                            size="small"
                          >
                            {isFieldApplicable(calculationResult.parameters.protectionType, 'systemVoltage') && calculationResult.parameters.systemVoltage != null && (
                              <Descriptions.Item label={getParamLabel('systemVoltage', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.systemVoltage} kV
                              </Descriptions.Item>
                            )}
                            {isFieldApplicable(calculationResult.parameters.protectionType, 'ratedCurrent') && calculationResult.parameters.ratedCurrent != null && (
                              <Descriptions.Item label={getParamLabel('ratedCurrent', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.ratedCurrent} A
                              </Descriptions.Item>
                            )}
                            {isFieldApplicable(calculationResult.parameters.protectionType, 'ctRatio') && calculationResult.parameters.ctRatio != null && (
                              <Descriptions.Item label={getParamLabel('ctRatio', calculationResult.parameters.protectionType)}>
                                1:{calculationResult.parameters.ctRatio}
                              </Descriptions.Item>
                            )}
                            {isFieldApplicable(calculationResult.parameters.protectionType, 'ptRatio') && calculationResult.parameters.ptRatio != null && (
                              <Descriptions.Item label={getParamLabel('ptRatio', calculationResult.parameters.protectionType)}>
                                1:{calculationResult.parameters.ptRatio}
                              </Descriptions.Item>
                            )}
                            {isFieldApplicable(calculationResult.parameters.protectionType, 'faultCurrent') && calculationResult.parameters.faultCurrent != null && (
                              <Descriptions.Item label={getParamLabel('faultCurrent', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.faultCurrent} A
                              </Descriptions.Item>
                            )}
                            {calculationResult.parameters.transformerCapacity && (
                              <Descriptions.Item label={getParamLabel('transformerCapacity', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.transformerCapacity} kVA
                              </Descriptions.Item>
                            )}
                            <Descriptions.Item label={getParamLabel('protectionType', calculationResult.parameters.protectionType)}>
                              {calculationResult.protectionTypeName}
                            </Descriptions.Item>
                            {['overcurrent', 'directional', 'zero_sequence'].includes(calculationResult.parameters.protectionType) && (
                              <>
                                <Descriptions.Item label={getParamLabel('curveType', calculationResult.parameters.protectionType)}>
                                  {mapCurveLabel(calculationResult.parameters.curveType)}
                                </Descriptions.Item>
                                <Descriptions.Item label={getParamLabel('timeMultiplier', calculationResult.parameters.protectionType)}>
                                  {calculationResult.parameters.timeMultiplier}
                                </Descriptions.Item>
                              </>
                            )}
                            {calculationResult.parameters.protectionType === 'directional' && (
                              <Descriptions.Item label={getParamLabel('polarizingAngle', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.polarizingAngle}
                              </Descriptions.Item>
                            )}
                            {calculationResult.parameters.protectionType === 'zero_sequence' && (
                              <Descriptions.Item label={getParamLabel('k0', calculationResult.parameters.protectionType)}>
                                {calculationResult.parameters.k0}
                              </Descriptions.Item>
                            )}
                            {calculationResult.parameters.protectionType === 'distance' && (
                              <>
                                <Descriptions.Item label={getParamLabel('lineAngleDeg', calculationResult.parameters.protectionType)}>
                                  {calculationResult.parameters.lineAngleDeg ?? calculationResult?.results?.lineAngleDeg}
                                </Descriptions.Item>
                                {(calculationResult.parameters.lineR_ohm != null || calculationResult.parameters.lineX_ohm != null) && (
                                  <Descriptions.Item label={getParamLabel('lineRX_ohm', calculationResult.parameters.protectionType)}>
                                    {`${calculationResult.parameters.lineR_ohm ?? '-'} / ${calculationResult.parameters.lineX_ohm ?? '-'}`}
                                  </Descriptions.Item>
                                )}
                                {calculationResult.parameters.selectedFeederId && (
                                  <Descriptions.Item label={getParamLabel('selectedFeederId', calculationResult.parameters.protectionType)}>
                                    {calculationResult.parameters.selectedFeederId}
                                  </Descriptions.Item>
                                )}
                              </>
                            )}
                          </Descriptions>
                          {formulaEvaluations && (
                            <div className="formula-section" style={{ marginTop: 16, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                              <Descriptions title="计算公式与计算过程" column={1} bordered size="small">
                                {formulaEvaluations.items.map((it, idx) => (
                                  <Descriptions.Item 
                                    key={idx} 
                                    label={(
                                      <span>
                                        {it.text}
                                        {it.explain ? (
                                          <Text type="secondary">（{it.explain}）</Text>
                                        ) : null}
                                      </span>
                                    )}
                                  >
                                    <Space direction="vertical" size={0}>
                                      <Text type="secondary">{it.substituted}</Text>
                                      <Text strong>{it.result}</Text>
                                    </Space>
                                  </Descriptions.Item>
                                ))}
                              </Descriptions>
                            </div>
                          )}

                          <Divider />
                          
                          <Descriptions 
                             title={
                               <span>
                                 计算结果
                                 <Tag color="blue" style={{ marginLeft: 12 }}>
                                   {getCalculationFormula(calculationResult.parameters.protectionType).title}
                                 </Tag>
                               </span>
                             } 
                             column={2} 
                             bordered
                             size="small"
                           >
                             {calculationResult.results && Object.entries(calculationResult.results)
                               .filter(([key]) => key !== 'curvePoints')
                               .map(([key, value]) => (
                                 <Descriptions.Item 
                                   key={key} 
                                   label={formatResultLabel(key, calculationResult.parameters.protectionType)}
                                 >
                                   {formatResultValue(key, value)}
                                 </Descriptions.Item>
                               ))}
                           </Descriptions>

                          {(() => {
                            try {
                              const barData = Object.entries(calculationResult?.results || {})
                                .filter(([k, v]) => typeof v === 'number' && isFinite(v))
                                .slice(0, 5)
                                .map(([k, v]) => ({ label: formatResultLabel(k, calculationResult.parameters.protectionType), value: v }));
                              if (barData.length === 0) return null;
                              return (
                                <div style={{ marginTop: 12 }}>
                                  <Title level={5} style={{ marginTop: 0 }}>条形图</Title>
                                  <BarChartMini data={barData} height={220} />
                                </div>
                              );
                            } catch (_) { return null; }
                          })()}

                           {/* 工程校核提示 */}
                           <div style={{ marginTop: 16 }}>
                            <Card title="工程校核提示" extra={(
                              <Radio.Group size="small" value={warningFilter} onChange={(e) => setWarningFilter(e.target.value)}>
                                <Radio.Button value="all">全部</Radio.Button>
                                <Radio.Button value="readability">仅可读性</Radio.Button>
                                <Radio.Button value="nonreadability">排除可读性</Radio.Button>
                              </Radio.Group>
                            )}>
                              {(() => {
                                const raw = Array.isArray(calculationResult.engineeringWarnings) ? calculationResult.engineeringWarnings : [];
                                const filtered = raw.filter((w) => {
                                  const isReadable = String(w).includes('【可读性】');
                                  if (warningFilter === 'readability') return isReadable;
                                  if (warningFilter === 'nonreadability') return !isReadable;
                                  return true;
                                });
                                return (
                                  filtered.length > 0 ? (
                                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                                      {filtered.map((w, idx) => (
                                        <li key={idx} style={{ marginBottom: 6 }}>
                                          <Text type="warning"><WarningOutlined /> {w}</Text>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <Space>
                                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                      <Text>校核通过，未发现显著风险</Text>
                                    </Space>
                                  )
                                );
                              })()}
                            </Card>
                          </div>

                          
    
                          {calculationResult?.results?.curvePoints && (
                            <div style={{ marginTop: 16 }}>
                              <Title level={5} style={{ marginTop: 0 }}>反时限曲线</Title>
                              <CurveChart points={calculationResult.results.curvePoints} />
                           </div>
                         )}
                         {calculationResult?.parameters?.protectionType === 'distance' && Array.isArray(calculationResult?.results?.zones) && calculationResult.results.zones.length > 0 && (
                           <div style={{ marginTop: 16 }}>
                             <Title level={5} style={{ marginTop: 0 }}>阻抗平面</Title>
                             <ZPlaneMini zones={calculationResult.results.zones} lineAngleDeg={calculationResult.results.lineAngleDeg ?? calculationResult.parameters.lineAngleDeg} />
                           </div>
                         )}
                         {/* 条形图已移动到结果摘要后并缩小尺寸 */}
   
                         {/* 选择性校核与曲线叠加 */}
                         <div style={{ marginTop: 16 }}>
                           <Card title="选择性校核与曲线叠加">
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <Space wrap>
                                  <Button onClick={addCurrentSeries} disabled={!calculationResult} data-testid="addCurrentSeries">添加当前曲线</Button>
                          <Select
                            style={{ minWidth: 260 }}
                            placeholder={t('calc.overlay.add_from_history.placeholder')}
                            onChange={(val) => addHistorySeries(val)}
                            options={(function(){
                              const opts = [];
                              try {
                                const local = getCurrentCalculationCache();
                                const lr = local?.results || local?.result?.results || {};
                                if (lr && lr.axesType) {
                                  opts.push({ value: 'local_current', label: (local?.result?.name && local.result.name.trim()) ? local.result.name.trim() : t('calc.overlay.local_current') });
                                }
                              } catch {}
                              const hist = (historyCandidates || [])
                                .filter(rec => {
                                  const res = rec?.results || rec?.result?.results || {};
                                  // 允许无结果但有参数的记录出现，选择后会回算
                                  return !!res.axesType || !!rec?.parameters;
                                })
                                .map(rec => ({
                                  value: rec.id,
                                  label: (rec.result?.name && rec.result.name.trim()) ? rec.result.name.trim() : `记录 ${String(rec.id || '').slice(0, 8)}`
                                }));
                              return [...opts, ...hist];
                            })()}
                          />
                          {((!userId) || (typeof navigator !== 'undefined' && navigator.onLine === false)) && (
                            <Button onClick={() => { try { clearHistoryCache(); setHistoryCandidates([]); message.success('已清空离线缓存'); } catch (_) { message.error('清空缓存失败'); } }} danger>
                              清空缓存
                            </Button>
                          )}
                          {(function(){
                            const hasOptions = (() => {
                              try {
                                const local = getCurrentCalculationCache();
                                const lr = local?.results || local?.result?.results || {};
                                if (lr && lr.axesType) return true;
                              } catch {}
                              const cnt = (historyCandidates || []).filter(rec => {
                                const res = rec?.results || rec?.result?.results || {};
                                return !!res.axesType;
                              }).length;
                              return cnt > 0;
                            })();
                            return !hasOptions;
                          })() && (
                            <Alert type="info" showIcon message={t('calc.overlay.history_empty_guidance')} />
                          )}
                                  <Button onClick={clearOverlay} danger>{t('calc.overlay.clear')}</Button>
                                </Space>
                                {overlaySeries.length > 0 && (() => {
                                  const colored = (overlaySeries || []).map(s => ({ ...s, color: overlayColorMap[s.label] || s.color }));
                                  const visible = colored.filter(s => !overlayHiddenLabels.includes(s.label));
                                  return (
                                    <div ref={overlayContainerRef}>
                                      <CurveOverlay
                                        series={visible}
                                        showLegend={false}
                                        zoomX={overlayZoomX}
                                        zoomY={overlayZoomY}
                                        panX={overlayPanX}
                                        panY={overlayPanY}
                                        showTooltips={overlayTooltipsEnabled}
                                        enableBoxZoom={overlayBoxZoomEnabled}
                                        colorBlindMode={overlayColorBlindMode}
                                        refLineKeys={overlayRefLineKeys}
                                        onViewportChange={({ zoomX, zoomY, panX, panY }) => {
                                          setOverlayZoomX(zoomX); setOverlayZoomY(zoomY); setOverlayPanX(panX); setOverlayPanY(panY);
                                          mergeOverlaySettings({ zoomX, zoomY, panX, panY });
                                        }}
                                      />
                                    </div>
                                  );
                                })()}
                                {overlaySeries.length > 0 && (
                                  <>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <Space wrap style={{ width: '100%' }}>
                                      <Typography.Text>{t('calc.overlay.legend.show')}</Typography.Text>
                                      <Switch checked={overlayShowLegend} onChange={(v) => {
                                        setOverlayShowLegend(v);
                                        mergeOverlaySettings({ showLegend: v });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.zoomX')}</Typography.Text>
                                      <InputNumber min={1} max={8} step={0.5} value={overlayZoomX} onChange={(v) => {
                                        const val = typeof v === 'number' ? v : 1;
                                        setOverlayZoomX(val);
                                        mergeOverlaySettings({ zoomX: val });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.zoomY')}</Typography.Text>
                                      <InputNumber min={1} max={8} step={0.5} value={overlayZoomY} onChange={(v) => {
                                        const val = typeof v === 'number' ? v : 1;
                                        setOverlayZoomY(val);
                                        mergeOverlaySettings({ zoomY: val });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.panX')}</Typography.Text>
                                      <InputNumber min={-0.9} max={0.9} step={0.05} value={overlayPanX} onChange={(v) => {
                                        const val = typeof v === 'number' ? v : 0;
                                        setOverlayPanX(val);
                                        mergeOverlaySettings({ panX: val });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.panY')}</Typography.Text>
                                      <InputNumber min={-0.9} max={0.9} step={0.05} value={overlayPanY} onChange={(v) => {
                                        const val = typeof v === 'number' ? v : 0;
                                        setOverlayPanY(val);
                                        mergeOverlaySettings({ panY: val });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.tooltips')}</Typography.Text>
                                      <Switch checked={overlayTooltipsEnabled} onChange={(v) => {
                                        setOverlayTooltipsEnabled(v);
                                        mergeOverlaySettings({ showTooltips: v });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.box_zoom')}</Typography.Text>
                                      <Switch checked={overlayBoxZoomEnabled} onChange={(v) => {
                                        setOverlayBoxZoomEnabled(v);
                                        mergeOverlaySettings({ enableBoxZoom: v });
                                      }} />
                                      <Typography.Text>{t('calc.overlay.colorblind_mode')}</Typography.Text>
                                      <Switch checked={overlayColorBlindMode} onChange={(v) => {
                                        setOverlayColorBlindMode(v);
                                        mergeOverlaySettings({ colorBlindMode: v });
                                      }} />
                                      <Button onClick={() => {
                                        setOverlayZoomX(1); setOverlayZoomY(1); setOverlayPanX(0); setOverlayPanY(0);
                                        mergeOverlaySettings({ zoomX: 1, zoomY: 1, panX: 0, panY: 0 });
                                      }}>{t('calc.overlay.reset_view')}</Button>
                                    </Space>
                                    {overlayShowLegend && (
                                      <>
                                        <Divider style={{ margin: '12px 0' }} />
                                        <Typography.Title level={5} style={{ margin: 0 }}>{t('calc.overlay.legend.title')}</Typography.Title>
                                        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                                          {/* 全局与每对最小裕度设置 */}
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Typography.Text>最小裕度（全局）</Typography.Text>
                                            <InputNumber min={0} max={5} step={0.05} value={overlayMinMargin} onChange={(v) => {
                                              const val = typeof v === 'number' ? v : 0.3;
                                              setOverlayMinMargin(val);
                                              mergeOverlaySettings({ minMargin: val });
                                            }} />
                                            <Button size="small" onClick={() => {
                                              setOverlayMinMargins([]);
                                              mergeOverlaySettings({ minMargins: [] });
                                            }}>重置每对最小裕度</Button>
                                            <Button size="small" onClick={() => {
                                              try {
                                                const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
                                                const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
                                                const count = Math.max(visible.length - 1, 0);
                                                const arr = Array.from({ length: count }, () => overlayMinMargin);
                                                setOverlayMinMargins(arr);
                                                mergeOverlaySettings({ minMargins: arr });
                                              } catch {}
                                            }}>应用到全部对</Button>
                                          </div>
                                          {(function(){
                                            const colored = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
                                            const visible = colored.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
                                            const pairs = [];
                                            for (let i = 0; i < visible.length - 1; i++) {
                                              pairs.push({ idx: i, dn: visible[i]?.label, up: visible[i+1]?.label });
                                            }
                                            if (pairs.length === 0) return null;
                                            return (
                                              <div style={{ marginTop: 8 }}>
                                                <div style={{ fontWeight: 500, marginBottom: 4 }}>每对最小裕度设置</div>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                  {pairs.map(p => (
                                                    <div key={`mm-${p.idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                      <Typography.Text style={{ minWidth: 280 }}>{`下游：${p.dn} ／ 上游：${p.up}`}</Typography.Text>
                                                      <InputNumber
                                                        min={0}
                                                        max={5}
                                                        step={0.05}
                                                        value={typeof overlayMinMargins[p.idx] === 'number' ? overlayMinMargins[p.idx] : overlayMinMargin}
                                                        onChange={(v) => {
                                                          const val = typeof v === 'number' ? v : overlayMinMargin;
                                                          setOverlayMinMargins(prev => {
                                                            const next = [...prev];
                                                            // 保证长度覆盖当前 pairs 数量
                                                            for (let i = 0; i < pairs.length; i++) { if (typeof next[i] !== 'number') next[i] = overlayMinMargin; }
                                                            next[p.idx] = val;
                                                            return next;
                                                          });
                                                        }}
                                                      />
                                                    </div>
                                                  ))}
                                                </Space>
                                              </div>
                                            );
                                          })()}
                                          {(overlaySeries || []).map((s, idx) => (
                                            <div key={(s.key || s.label || idx)} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                              <Checkbox
                                                checked={!(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label))}
                                                onChange={(e) => {
                                                  const checked = e.target.checked;
                                                  setOverlayHiddenLabels((prev) => {
                                                    const set = new Set(prev);
                                                    const k = s.key || s.label;
                                                    if (checked) set.delete(k); else set.add(k);
                                                    return Array.from(set);
                                                  });
                                                }}
                                              >{s.label}</Checkbox>
                                              <Input
                                                size="small"
                                                value={s.label}
                                                aria-label={t('calc.overlay.legend.rename')}
                                                onChange={(e) => {
                                                  const nextLabel = e.target.value;
                                                  const k = s.key || s.label;
                                                  setOverlaySeries(prev => prev.map((it) => (it.key === k || it.label === s.label) ? { ...it, label: nextLabel } : it));
                                                  const nextMap = { ...overlayLabelOverrides, [k]: nextLabel };
                                                  setOverlayLabelOverrides(nextMap);
                                                  try { localStorage.setItem('tccOverlayLabels', JSON.stringify(nextMap)); } catch {}
                                                }}
                                              />
                                              <ColorPicker
                                                value={(overlayColorMap[s.key] || overlayColorMap[s.label] || s.color)}
                                                onChange={(color) => {
                                                  const hex = color.toHexString();
                                                  const k = s.key || s.label;
                                                  const next = { ...overlayColorMap, [k]: hex };
                                                  setOverlayColorMap(next);
                                                  try { localStorage.setItem('tccOverlayColors', JSON.stringify(next)); } catch {}
                                                }}
                                              />
                                              <Button size="small" onClick={() => {
                                                if (idx <= 0) return;
                                                setOverlaySeries(prev => {
                                                  const arr = [...prev];
                                                  const [item] = arr.splice(idx, 1);
                                                  arr.splice(idx - 1, 0, item);
                                                  // 同步持久化顺序（按 key）
                                                  try { localStorage.setItem('tccOverlayOrder', JSON.stringify(arr.map(it => it.key || it.label))); } catch {}
                                                  return arr;
                                                });
                                              }}>{t('calc.overlay.legend.move_up')}</Button>
                                              <Button size="small" onClick={() => {
                                                setOverlaySeries(prev => {
                                                  const arr = [...prev];
                                                  if (idx >= arr.length - 1) return arr;
                                                  const [item] = arr.splice(idx, 1);
                                                  arr.splice(idx + 1, 0, item);
                                                  try { localStorage.setItem('tccOverlayOrder', JSON.stringify(arr.map(it => it.key || it.label))); } catch {}
                                                  return arr;
                                                });
                                              }}>{t('calc.overlay.legend.move_down')}</Button>
                                              <Button size="small" type={overlayRefLineKeys.includes(s.key || s.label) ? 'primary' : 'default'} onClick={() => {
                                                const k = s.key || s.label;
                                                setOverlayRefLineKeys(prev => {
                                                  const set = new Set(prev);
                                                  if (set.has(k)) set.delete(k); else set.add(k);
                                                  return Array.from(set);
                                                });
                                              }}>{t('calc.overlay.legend.ref_line')}</Button>
                                            </div>
                                          ))}
                                          <Space>
                                            <Button onClick={() => {
                                              setOverlayHiddenLabels([]);
                                              try { localStorage.setItem('tccOverlayHidden', JSON.stringify([])); } catch {}
                                            }}>{t('calc.overlay.show_all')}</Button>
                                            <Button onClick={() => {
                                              try {
                                                const reset = {};
                                                (overlaySeries || []).forEach((s, idx) => { const k = s.key || s.label; reset[k] = ['#1677ff','#faad14','#52c41a','#eb2f96','#13c2c2','#722ed1','#f5222d','#2f54eb','#a0d911','#fa8c16'][idx % 10]; });
                                                setOverlayColorMap(reset);
                                                localStorage.setItem('tccOverlayColors', JSON.stringify(reset));
                                              } catch {}
                                            }}>{t('calc.overlay.reset_colors')}</Button>
                                            <Button type="primary" onClick={async () => {
                                              try {
                                                const el = overlayContainerRef.current;
                                                if (!el) return;
                                                const dataUrl = await captureElementToPngDataUrl(el);
                                                const a = document.createElement('a');
                                                a.href = dataUrl; a.download = `TCC_Overlay_${Date.now()}.png`; a.click();
                                              } catch (e) { console.error('导出叠加图失败', e); }
                                            }}>{t('calc.overlay.export_png')}</Button>
                                            <Button onClick={async () => {
                                              try {
                                                const lines = [];
                                                const visible = (overlaySeries || []).filter(s => !overlayHiddenLabels.includes(s.label));
                                                lines.push(t('calc.overlay.md.title'));
                                                lines.push(`${t('calc.overlay.md.count_prefix')}${visible.length}`);
                                                lines.push('');
                                                lines.push(t('calc.overlay.md.list_title'));
                                                visible.forEach((s, idx) => {
                                                  const role = idx === 0 ? t('calc.overlay.md.downstream') : t('calc.overlay.md.upstream');
                                                  lines.push(`- **${role}**：${s.label}，${t('calc.overlay.md.pickup_label')}=${s.pickup}A，${t('calc.overlay.md.tms_label')}=${s.TMS}，${t('calc.overlay.md.curve_label')}=${s.curveType}`);
                                                });
                                                const md = lines.join('\n');
                                                const filename = `TCC_Overlay_${Date.now()}.md`;
                                                downloadTextFile(md, filename, 'text/markdown;charset=utf-8');
                                              } catch (e) { console.error('导出 Markdown 失败', e); }
                                            }}>{t('calc.overlay.export_md')}</Button>
                                          </Space>
                                        </Space>
                                      </>
                                    )}
                                  </>
                                )}
                                {overlaySeries.length >= 2 && (() => {
                                  const coloredAll = (overlaySeries || []).map(s => ({ ...s, color: (overlayColorMap[s.key] || overlayColorMap[s.label] || s.color) }));
                                  const visible = coloredAll.filter(s => !(overlayHiddenLabels.includes(s.key) || overlayHiddenLabels.includes(s.label)));
                                  const seriesFromOrder = (selPanelEnabled && selPanelOrder.length >= 2)
                                    ? selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean)
                                    : visible;
                                  const downstream = seriesFromOrder[0];
                                  const upstream = seriesFromOrder[1];
                                  const mm0Global = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                                  const mm0 = (Array.isArray(selPanelMinMargins) && typeof selPanelMinMargins[0] === 'number') ? selPanelMinMargins[0] : mm0Global;
                                  const analysis = analyzeSelectivity(downstream, upstream, { minMargin: mm0 });
                                  const rec = recommendUpstreamTMS(
                                    { pickup: downstream.pickup, TMS: downstream.TMS, curveType: downstream.curveType },
                                    { pickup: upstream.pickup, TMS: upstream.TMS, curveType: upstream.curveType },
                                    { minMargin: mm0 }
                                  );
                                  return (
                                    <div style={{ marginTop: 12 }}>
                                      {/* 选择性校核面板（多层链路选择） */}
                                      <Card size="small" title="选择性校核面板（多层链路选择）" style={{ marginBottom: 8 }} extra={(
                                        <Space>
                                          <Checkbox checked={selPanelEnabled} onChange={(e) => setSelPanelEnabled(e.target.checked)}>启用面板接管矩阵</Checkbox>
                                          <Button size="small" onClick={() => {
                                            try {
                                              // 从叠加选中导入（仅可见序列）
                                              const order = visible.map(s => (s.key || s.label)).filter(Boolean);
                                              setSelPanelOrder(order);
                                              setSelPanelMinMargins(Array.from({ length: Math.max(0, order.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                              message?.success('已从叠加选中导入顺序');
                                            } catch (e) {
                                              console.error('导入顺序失败', e);
                                              message?.error('导入失败');
                                            }
                                          }}>从叠加选中导入</Button>
                                          <Button size="small" onClick={() => {
                                            // 按额定电流（pickup）升序排序（仅 I-t 序列）
                                            const byIt = (coloredAll || []).filter(s => s.axesType === 'I-t');
                                            const sorted = [...byIt].sort((a, b) => (a.pickup || 0) - (b.pickup || 0)).map(s => (s.key || s.label)).filter(Boolean);
                                            setSelPanelOrder(sorted);
                                            setSelPanelMinMargins(Array.from({ length: Math.max(0, sorted.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                            message?.success('已按额定电流升序排序');
                                          }}>按额定电流升序</Button>
                                          <Select
                                            size="small"
                                            mode="multiple"
                                            style={{ minWidth: 220 }}
                                            placeholder="选择曲线追加"
                                            value={selPanelPicker}
                                            onChange={(vals) => setSelPanelPicker(Array.isArray(vals) ? vals : [])}
                                            options={(coloredAll || [])
                                              .filter(s => s.axesType === 'I-t')
                                              .filter(s => !(selPanelOrder.includes(s.key) || selPanelOrder.includes(s.label)))
                                              .map(s => ({ value: (s.key || s.label), label: s.label }))}
                                          />
                                          <Button size="small" type="primary" onClick={() => {
                                            try {
                                              const picked = Array.isArray(selPanelPicker) ? selPanelPicker : [];
                                              if (picked.length === 0) { message?.info('请选择要追加的曲线'); return; }
                                              const nextOrder = [...selPanelOrder];
                                              picked.forEach(v => { if (v && !nextOrder.includes(v)) nextOrder.push(v); });
                                              setSelPanelOrder(nextOrder);
                                              setSelPanelMinMargins(Array.from({ length: Math.max(0, nextOrder.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                              setSelPanelPicker([]);
                                              message?.success('已追加到末尾');
                                            } catch (e) {
                                              console.error('追加失败', e);
                                              message?.error('追加失败');
                                            }
                                          }}>追加到末尾</Button>
                                          <Button size="small" onClick={() => {
                                            setSelPanelOrder([]);
                                            setSelPanelMinMargins([]);
                                            setSelPanelEnabled(false);
                                            message?.info('已清空选择性面板');
                                          }}>清空</Button>
                                        </Space>
                                      )}>
                                        {/* 已选顺序列表 */}
                                        {(selPanelOrder || []).length === 0 ? (
                                          <Text type="secondary">面板为空：可从叠加选中导入或按额定电流排序生成。</Text>
                                        ) : (
                                          <div>
                                            <div style={{ fontWeight: 500, marginBottom: 4 }}>已选顺序</div>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                              {selPanelOrder.map((k, idx) => {
                                                const s = coloredAll.find(x => (x.key || x.label) === k);
                                                const label = s?.label || k;
                                                return (
                                                  <div key={`ord-${k}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Tag color="blue" style={{ flex: 1 }}>{label}</Tag>
                                                    <Space>
                                                      <Button size="small" onClick={() => {
                                                        if (idx === 0) return;
                                                        const next = [...selPanelOrder];
                                                        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                                        setSelPanelOrder(next);
                                                        // 逐对最小裕度随顺序改变，仅保留相对索引，长度重算
                                                        setSelPanelMinMargins(Array.from({ length: Math.max(0, next.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                                      }}>上移</Button>
                                                      <Button size="small" onClick={() => {
                                                        if (idx === selPanelOrder.length - 1) return;
                                                        const next = [...selPanelOrder];
                                                        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                                                        setSelPanelOrder(next);
                                                        setSelPanelMinMargins(Array.from({ length: Math.max(0, next.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                                      }}>下移</Button>
                                                      <Button size="small" danger onClick={() => {
                                                        const next = selPanelOrder.filter((_, i) => i !== idx);
                                                        setSelPanelOrder(next);
                                                        setSelPanelMinMargins(Array.from({ length: Math.max(0, next.length - 1) }, (_, i) => selPanelMinMargins[i]));
                                                      }}>移除</Button>
                                                    </Space>
                                                  </div>
                                                );
                                              })}
                                            </Space>
                                            <div style={{ marginTop: 8 }}>
                                              <Space align="center">
                                                <div style={{ color: '#666' }}>最小选择性裕度（全局）</div>
                                                <InputNumber min={0.01} max={10} step={0.01} value={selPanelGlobalMinMargin} onChange={(v) => setSelPanelGlobalMinMargin(typeof v === 'number' ? v : selPanelGlobalMinMargin)} />
                                                <Button size="small" onClick={() => setSelPanelGlobalMinMargin(0.3)}>重置为 0.3s</Button>
                                              </Space>
                                            </div>
                                            {/* 逐对最小裕度 */}
                                            {(() => {
                                              const panelSeries = selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean);
                                              const pairs = panelSeries.slice(0, -1).map((dn, i) => ({ dn, up: panelSeries[i + 1], idx: i }));
                                              if (pairs.length === 0) return null;
                                              return (
                                                <div style={{ marginTop: 8 }}>
                                                  <div style={{ fontWeight: 500, marginBottom: 4 }}>逐对最小裕度</div>
                                                  <Space direction="vertical" style={{ width: '100%' }}>
                                                    {pairs.map(({ dn, up, idx }) => (
                                                      <div key={`mm-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div style={{ flex: 1 }}>{dn?.label || '下游'} → {up?.label || '上游'}</div>
                                                        <InputNumber
                                                          min={0.01}
                                                          max={10}
                                                          step={0.01}
                                                          placeholder={String(selPanelGlobalMinMargin)}
                                                          value={typeof selPanelMinMargins[idx] === 'number' ? selPanelMinMargins[idx] : undefined}
                                                          onChange={(v) => {
                                                            const next = [...selPanelMinMargins];
                                                            next[idx] = typeof v === 'number' ? v : undefined;
                                                            setSelPanelMinMargins(next);
                                                          }}
                                                        />
                                                        <Button size="small" onClick={() => {
                                                          const next = [...selPanelMinMargins];
                                                          next[idx] = undefined;
                                                          setSelPanelMinMargins(next);
                                                        }}>重置为全局</Button>
                                                      </div>
                                                    ))}
                                                  </Space>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </Card>
                                      <Descriptions bordered size="small" column={2} title="选择性校核结果">
                                        <Descriptions.Item label="最差裕度">
                                          {analysis.worstMargin === null ? '—' : `${analysis.worstMargin}s`} （发生在 I≈{analysis.worstAtI || '—'}A）
                                        </Descriptions.Item>
                                        <Descriptions.Item label={`是否满足 ${mm0}s 裕度`}>
                                          {analysis.ok ? '是' : '否'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="建议上级 TMS">
                                          {rec.recommended ? rec.recommended : '—'} {rec.capped ? '（受限于最大 1.00）' : ''}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="说明">
                                          下级以当前结果为基准，上级为所选历史曲线；建议值用于保证高故障电流区间的时间选择性。
                                        </Descriptions.Item>
                                      </Descriptions>
                                      <div style={{ marginTop: 8 }}>
                                        <Space>
                                          <Button
                                            size="small"
                                            type="primary"
                                            disabled={!(typeof rec.recommended === 'number')}
                                            onClick={() => {
                                              try {
                                                const targetLabel = upstream?.label;
                                                const targetKey = upstream?.key;
                                                const nextTms = rec.recommended;
                                                if (typeof nextTms === 'number') {
                                                  setOverlaySeries(prev => prev.map(s => {
                                                    const match = (targetKey && s.key === targetKey) || (targetLabel && s.label === targetLabel);
                                                    return match ? { ...s, TMS: nextTms } : s;
                                                  }));
                                                  message?.success('已应用建议 TMS 到上级曲线');
                                                }
                                              } catch (e) {
                                                console.error('应用建议 TMS 失败', e);
                                                message?.error('应用失败，请重试');
                                              }
                                            }}
                                          data-testid="applyUpstreamTms">应用建议到上级 TMS</Button>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              try {
                                                const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
                                                  ? selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean)
                                                  : visible;
                                                const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                                                const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
                                                const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
                                                const adjMap = new Map();
                                                (matrix.adjustments || []).forEach(adj => { if (typeof adj.to === 'number') adjMap.set(adj.label, adj.to); });
                                                if (adjMap.size === 0) { message?.info('暂无顺序调整建议'); return; }
                                                setOverlaySeries(prev => prev.map(s => adjMap.has(s.label) ? { ...s, TMS: adjMap.get(s.label) } : s));
                                                message?.success('已应用顺序调整建议');
                                              } catch (e) {
                                                console.error('应用顺序调整建议失败', e);
                                                message?.error('应用失败，请重试');
                                              }
                                            }}
                                          data-testid="applySequentialAdjust">一键应用顺序调整建议</Button>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              try {
                                                const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
                                                  ? selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean)
                                                  : visible;
                                                const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                                                const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
                                                const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
                                                const { md } = buildSelectivityMatrixMarkdown(matrix, {
                                                  title: t ? (t('export.report.selectivity.matrix_title') || '多级选择性校核矩阵') : '多级选择性校核矩阵',
                                                  minMargin: mmForMatrix,
                                                  context: { project: (project || '').trim(), line: (line || '').trim(), versionStamp: generateVersionStamp() },
                                                  t
                                                });
                                                const sortText = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
                                                const prefixResolved = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortText });
                                                const finalBase = buildFinalBaseName((calcName || '').trim(), prefixResolved);
                                                downloadTextFile(md, `${finalBase}-selectivity-matrix.md`, 'text/markdown;charset=utf-8');
                                              } catch (e) {
                                                console.error('导出矩阵 Markdown 失败', e);
                                                message?.error('导出失败');
                                              }
                                            }}
                                          data-testid="exportMatrixMarkdown">导出矩阵 Markdown</Button>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              try {
                                                const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
                                                  ? selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean)
                                                  : visible;
                                                const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                                                const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins, sequentialAdjust: true });
                                                const { csv } = buildSelectivityMatrixCsv(matrix, { minMargin: mmForMatrix, t });
                                                const sortText2 = selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序';
                                                const prefixResolved2 = resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: sortText2 });
                                                const finalBase2 = buildFinalBaseName((calcName || '').trim(), prefixResolved2);
                                                downloadTextFile(csv, `${finalBase2}-selectivity-matrix.csv`, 'text/csv;charset=utf-8');
                                              } catch (e) {
                                                console.error('导出矩阵 CSV 失败', e);
                                                message?.error('导出失败');
                                              }
                                            }}
                                          data-testid="exportMatrixCsv">导出矩阵 CSV</Button>
                                        </Space>
                                      </div>
                                      {(() => {
                                        try {
                                          const seriesForMatrix = (selPanelEnabled && selPanelOrder.length >= 2)
                                            ? selPanelOrder.map(k => coloredAll.find(s => (s.key || s.label) === k)).filter(Boolean)
                                            : visible;
                                          const mmForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelGlobalMinMargin : overlayMinMargin;
                                          const arrForMatrix = (selPanelEnabled && selPanelOrder.length >= 2) ? selPanelMinMargins : overlayMinMargins;
                                          const matrix = buildSelectivityMatrix(seriesForMatrix, { minMargin: mmForMatrix, minMargins: arrForMatrix, sequentialAdjust: true });
                                          const adjMap = new Map();
                                          (matrix.adjustments || []).forEach(adj => { if (typeof adj.to === 'number') adjMap.set(adj.label, adj.to); });
                                          return (
                                            <div style={{ marginTop: 8 }}>
                                              <div style={{ fontWeight: 500, marginBottom: 4 }}>多级选择性校核矩阵（相邻链路）</div>
                                              <div style={{ color: '#666', marginBottom: 6 }}>最小裕度（全局）：{mmForMatrix}s；总体是否满足：{matrix.overallOk ? '是' : '否'}</div>
                                              <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                  <thead>
                                                    <tr>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '4px 6px' }}>下游</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'left', padding: '4px 6px' }}>上游</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '4px 6px' }}>最差裕度(s)</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '4px 6px' }}>发生在 I(A)</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '4px 6px' }}>最小裕度(s)</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'center', padding: '4px 6px' }}>是否满足</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'right', padding: '4px 6px' }}>建议上级 TMS</th>
                                                      <th style={{ borderBottom: '1px solid #e9e9e9', textAlign: 'center', padding: '4px 6px' }}>操作</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {(matrix.pairs || []).map((p, idx) => {
                                                      const pairIndex = seriesForMatrix.findIndex(s => (s.label === p.downstream));
                                                      const isEditable = pairIndex >= 0;
                                                      const currentArr = arrForMatrix || [];
                                                      const currentVal = (isEditable && typeof currentArr[pairIndex] === 'number') ? currentArr[pairIndex] : mmForMatrix;
                                                      const rowStyle = p.ok ? undefined : { background: '#fff6f6' };
                                                      return (
                                                        <tr key={`mx-${idx}`} style={rowStyle}>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 6px' }}>{p.downstream}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 6px' }}>{p.upstream}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '4px 6px' }}>{p.worstMargin == null ? '—' : String(p.worstMargin)}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '4px 6px' }}>{p.worstAtI == null ? '—' : String(p.worstAtI)}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '4px 6px' }}>
                                                            {isEditable ? (
                                                              <InputNumber
                                                                size="small"
                                                                min={0}
                                                                max={5}
                                                                step={0.05}
                                                                value={currentVal}
                                                                onChange={(v) => {
                                                                  const val = typeof v === 'number' ? v : mmForMatrix;
                                                                  if (selPanelEnabled && selPanelOrder.length >= 2) {
                                                                    const next = [...selPanelMinMargins];
                                                                    for (let i = 0; i < seriesForMatrix.length - 1; i++) { if (typeof next[i] !== 'number') next[i] = mmForMatrix; }
                                                                    next[pairIndex] = val;
                                                                    setSelPanelMinMargins(next);
                                                                  } else {
                                                                    setOverlayMinMargins(prev => {
                                                                      const next = [...prev];
                                                                      for (let i = 0; i < seriesForMatrix.length - 1; i++) { if (typeof next[i] !== 'number') next[i] = mmForMatrix; }
                                                                      next[pairIndex] = val;
                                                                      return next;
                                                                    });
                                                                    mergeOverlaySettings({ minMargins: (function(prev){
                                                                      const next = Array.isArray(prev) ? [...prev] : [];
                                                                      for (let i = 0; i < seriesForMatrix.length - 1; i++) { if (typeof next[i] !== 'number') next[i] = mmForMatrix; }
                                                                      next[pairIndex] = val;
                                                                      return next;
                                                                    })(overlayMinMargins) });
                                                                  }
                                                                }}
                                                              />
                                                            ) : (p.minMargin == null ? '—' : String(p.minMargin))}
                                                          </td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'center', padding: '4px 6px' }}>{p.ok ? '是' : '否'}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'right', padding: '4px 6px' }}>{(p.recommendation && typeof p.recommendation.recommended === 'number') ? `${p.recommendation.recommended}${p.recommendation.capped ? '（受限）' : ''}` : '—'}</td>
                                                          <td style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'center', padding: '4px 6px' }}>
                                                            <Button size="small" disabled={p.ok || !adjMap.has(p.upstream)} onClick={() => {
                                                              const to = adjMap.get(p.upstream);
                                                              if (typeof to !== 'number') { message?.info('该行无建议'); return; }
                                                              setOverlaySeries(prev => prev.map(s => (s.label === p.upstream) ? { ...s, TMS: to } : s));
                                                              message?.success('已应用该行建议到上级 TMS');
                                                            }}>应用建议</Button>
                                                          </td>
                                                        </tr>
                                                      );
                                                    })}
                                                  </tbody>
                                                </table>
                                              </div>
                                              {Array.isArray(matrix.adjustments) && matrix.adjustments.length > 0 && (
                                                <div style={{ marginTop: 6, color: '#666' }}>
                                                  顺序调整建议：{matrix.adjustments.map((adj, i) => `${i > 0 ? '； ' : ''}上级“${adj.label}” TMS：${adj.from} → ${adj.to}${adj.capped ? '（受限）' : ''}`)}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        } catch {
                                          return null;
                                        }
                                      })()}
                                    </div>
                                  );
                                })()}
                              </Space>
                            </Card>
                          </div>
                          
                          {/* 差动特性分段备注展示 */}
                          {(calculationResult?.parameters?.protectionType === 'differential' && Array.isArray(calculationResult?.results?.characteristic?.segments)) && (
                            <div style={{ marginTop: 16 }}>
                              <Card title="差动特性分段备注" extra={(
                                <Space size={8}>
                                  <Checkbox className="print-hide" checked={includeProjectLineInCopy} onChange={(e) => setIncludeProjectLineInCopy(e.target.checked)}>包含项目/线路</Checkbox>
                                  <Button size="small" onClick={async () => {
                                  try {
                                    const segs = calculationResult?.results?.characteristic?.segments || [];
                                    const nowStr = new Date().toLocaleString('zh-CN');
                                    const headerLines = [
                                      '差动特性分段备注',
                                      `名称：${(calcName || '').trim() || '未命名'}`,
                                      '类型：差动保护',
                                    ];
                                    // 可选注入项目/线路（统一为独立行；顺序在时间之前）
                                    if (includeProjectLineInCopy) {
                                      const pj = (project || '').trim();
                                      const ln = (line || '').trim();
                                      if (pj) headerLines.push(`项目：${pj}`);
                                      if (ln) headerLines.push(`线路：${ln}`);
                                    }
                                    headerLines.push(`时间：${nowStr}`);
                                    const lines = segs.map((seg, idx) => {
                                      const ir = seg?.threshold === Infinity ? '∞' : seg?.threshold;
                                      const note = (seg?.note ?? '').trim();
                                      const label = seg?.label ?? `SEG${idx + 1}`;
                                      const k = typeof seg?.slope === 'number' ? seg.slope : '-';
                                      return `段${idx + 1}（${label}）：K=${k}，Ir=${ir}${note ? `；备注：${note}` : ''}`;
                                    });
                                    const text = [...headerLines, '', ...lines].join('\n');
                                    await navigator.clipboard.writeText(text);
                                    message?.success('分段备注已复制到剪贴板');
                                  } catch (e) {
                                    console.error('复制备注失败', e);
                                    message?.error('复制失败，请重试');
                                  }
                                }}>复制备注</Button>
                                </Space>
                              )}>
                                {(() => {
                                  const segs = calculationResult?.results?.characteristic?.segments || [];
                                  if (segs.length === 0) return (<Text type="secondary">暂无分段</Text>);
                                  return (
                                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                                      {segs.map((seg, idx) => {
                                        const ir = seg?.threshold === Infinity ? '∞' : seg?.threshold;
                                        const note = (seg?.note ?? '').trim();
                                        return (
                                          <li key={idx} style={{ marginBottom: 6 }}>
                                            <Text>
                                              段 {idx + 1}（{seg?.label ?? '未命名'}）：K={seg?.slope ?? '-'}，Ir={ir}
                                            </Text>
                                            {note && (
                                              <div style={{ marginTop: 2 }}>
                                                <Text type="secondary">备注：{note}</Text>
                                              </div>
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  );
                                })()}
                              </Card>
                            </div>
                          )}
                          
                          {calculationResult.results.sensitivity && (
                            <>
                              <Divider />
                              <div className="sensitivity-section">
                                <Title level={5}>灵敏度校验</Title>
                                <div className="sensitivity-progress">
                                  <Text>灵敏度系数: {calculationResult.results.sensitivity}</Text>
                                  <Progress 
                                    percent={Math.min(calculationResult.results.sensitivity * 20, 100)} 
                                    status={calculationResult.results.sensitivity >= 2 ? "success" : "exception"}
                                  />
                                  <Text type={calculationResult.results.sensitivity >= 2 ? "success" : "danger"}>
                                    {calculationResult.results.sensitivity >= 2 ? 
                                      "灵敏度满足要求 (≥2)" : 
                                      "灵敏度不满足要求 (<2)"}
                                  </Text>
                                </div>
                              </div>
                            </>
                          )}
                        </Card>
                      </Col>
                      
                      <Col xs={24} lg={8}>
                        <Card title="结果说明" className="explanation-section">
                          {(() => {
                            const explanation = getResultExplanation(calculationResult.parameters.protectionType);
                            return (
                              <>
                                <Title level={5}>{explanation.title}</Title>
                                <Paragraph>
                                  {explanation.content}
                                </Paragraph>
                                
                                <div className="warning-section">
                                  <Title level={5} style={{ color: '#faad14' }}>
                                    <WarningOutlined /> 注意事项
                                  </Title>
                                  <Paragraph type="warning">
                                    {explanation.warning}
                                  </Paragraph>
                                </div>
                                
                                <div className="standard-reference">
                                  <Tag color="blue">IEC 60255</Tag>
                                  <Tag color="green">GB/T 14285</Tag>
                                  <Tag color="purple">DL/T 584</Tag>
                                </div>
                              </>
                            );
                          })()}
                        </Card>
                        
                        <Card title="操作建议" className="action-section" style={{ marginTop: 16 }}>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Input 
                              value={calcName}
                              onChange={(e) => setCalcName(e.target.value)}
                              placeholder="请输入计算名称"
                              maxLength={60}
                              disabled={viewMode}
                            />
                            <Input 
                              value={project}
                              onChange={(e) => setProject(e.target.value)}
                              placeholder="项目（可选）"
                              maxLength={80}
                              disabled={viewMode}
                            />
                            <Input 
                              value={line}
                              onChange={(e) => setLine(e.target.value)}
                              placeholder="线路（可选）"
                              maxLength={80}
                              disabled={viewMode}
                            />
                            <Input.TextArea
                              value={remark}
                              onChange={(e) => setRemark(e.target.value)}
                              placeholder="备注（可选）"
                              autoSize={{ minRows: 2, maxRows: 4 }}
                              maxLength={300}
                              disabled={viewMode}
                            />
                            <Button 
                              type="primary" 
                              block
                              disabled={viewMode}
                              onClick={() => setActiveTab('input')}
                            >
                              重新计算
                            </Button>
                            <Button 
                              block
                              disabled={!userId || viewMode || !calcName.trim()}
                              onClick={handleSaveResult}
                            >保存结果</Button>
                            <Dropdown
                              menu={{
                                items: [
                                  { key: 'md', label: '导出 Markdown' },
                                  { key: 'html', label: '导出 HTML' },
                                  { key: 'word', label: '导出 Word' },
                                  ...(ENABLE_CALC_REPORT_PDF_EXPORT ? [{ key: 'pdf_report', label: '导出 PDF' }] : []),
                                  { key: 'pdf', label: '导出页面' }
                                ],
                                onClick: ({ key }) => {
                                  if (!userId || !calcName.trim()) {
                                    message.warning('请先登录并填写名称');
                                    return;
                                  }
                                  switch (key) {
                                    case 'md':
                                      handleExportReport();
                                      break;
                                    case 'html':
                                      handleExportHtmlReport();
                                      break;
                                    case 'word':
                                      handleExportWordReport();
                                      break;
                                    case 'pdf':
                                      handleExportPDF();
                                      break;
                                    case 'pdf_report':
                                      handleExportPDFReport();
                                      break;
                                    default:
                                      break;
                                  }
                                }
                              }}
                            >
                              <Button block disabled={!userId || !calcName.trim()}>
                                导出 <DownOutlined style={{ marginLeft: 8 }} />
                              </Button>
                            </Dropdown>
                            <Divider style={{ margin: '12px 0' }} />
                            <div>
                              <div style={{ fontWeight: 500, marginBottom: 6 }}>导出选项</div>
                              <Space wrap>
                                <Checkbox checked={exportIncludeParamNotes} onChange={(e) => setExportIncludeParamNotes(e.target.checked)}>包含参数说明</Checkbox>
                                <Checkbox checked={exportIncludeDeviceSource} onChange={(e) => setExportIncludeDeviceSource(e.target.checked)}>包含设备来源</Checkbox>
                                <Checkbox checked={exportIncludeSelectivity} onChange={(e) => setExportIncludeSelectivity(e.target.checked)}>包含选择性矩阵</Checkbox>
                              </Space>
                              <div style={{ marginTop: 8 }}>
                                <span style={{ color: '#666', marginRight: 8 }}>文件名前缀模板</span>
                                <Input style={{ width: 360 }}
                                  placeholder="如：{name}-{project}-{line}-{date}"
                                  value={exportFileNamePrefix}
                                  onChange={(e) => setExportFileNamePrefix(e.target.value)}
                                />
                                <div style={{ color: '#999', marginTop: 4 }}>
                                  预览：{resolveFilenamePrefix(exportFileNamePrefix || '', { name: (calcName || '').trim(), project: (project || '').trim(), line: (line || '').trim(), sort: selPanelEnabled ? `按面板选中顺序（${(selPanelOrder || []).join(' → ')}）` : '按当前视图顺序' }) || '（空）'}
                                </div>
                              </div>
                            </div>
                            <Divider style={{ margin: '12px 0' }} />
                            <div>
                              <div style={{ fontWeight: 500, marginBottom: 6 }}>分享策略</div>
                              <Space wrap>
                                <Checkbox checked={sharePreferShort} onChange={(e) => setSharePreferShort(e.target.checked)}>优先短链接</Checkbox>
                                <Checkbox checked={shareRequiresLogin} onChange={(e) => setShareRequiresLogin(e.target.checked)}>需要登录</Checkbox>
                                <span style={{ color: '#666' }}>最大访问次数</span>
                                <InputNumber min={1} max={100} step={1} value={shareMaxUses} onChange={(v) => setShareMaxUses(typeof v === 'number' ? v : shareMaxUses)} />
                              </Space>
                            </div>
                            <Button 
                              block
                              disabled={!calcName.trim()}
                              onClick={handleGenerateShareLink}
                            >
                              生成分享链接
                            </Button>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              )
            }
          ]}
        />
        {/* 自动整定向导 Modal */}
        <AutoTuningWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          initialValues={wizardInitialValues || (() => { try { return form.getFieldsValue(); } catch (_) { return {}; } })()}
          onApply={applyWizardSuggestions}
        />
      </Card>
    </div>
  );
};

// 结果标签统一从 exportUtils.formatResultLabel 获取

// 格式化结果值（底层统一用 exportUtils.formatResultValueExport，再结合 UI Tag）
const formatResultValue = (key, value) => {
  const base = formatResultValueExport(key, value);
  if (key === 'curveType') {
    return <Tag color="blue">{String(base)}</Tag>;
  }

  if (key === 'reach') {
    return <Tag color="green">{base}</Tag>;
  }

  if (key === 'operatingCharacteristic') {
    return <Tag color="purple">{base}</Tag>;
  }

  if (key === 'direction') {
    return <Tag color={base === '正向' ? 'blue' : 'orange'}>{base}</Tag>;
  }

  if (key === 'operateTime') {
    if (base === '不动作') return <Tag>不动作</Tag>;
  }

  return base;
};

// 反时限曲线SVG渲染组件
const CurveChart = ({ points }) => {
  const width = 600;
  const height = 220;
  const padding = { left: 40, right: 16, top: 16, bottom: 24 };
  const xs = points.map(p => p.M);
  const ys = points.map(p => (p.t === Infinity ? 0 : p.t));
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMax = Math.max(...ys);
  const xScale = (x) => padding.left + (x - xMin) / (xMax - xMin) * (width - padding.left - padding.right);
  const yScale = (y) => height - padding.bottom - (y / yMax) * (height - padding.top - padding.bottom);
  const pathD = points
    .filter(p => p.t !== Infinity)
    .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(p.M)},${yScale(p.t)}`)
    .join(' ');
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={width} height={height} style={{ background: '#fff', border: '1px solid #eee' }}>
        {/* 坐标轴 */}
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#999" strokeWidth="1" />
        <line x1={padding.left} y1={height - padding.bottom} x2={padding.left} y2={padding.top} stroke="#999" strokeWidth="1" />
        {/* 曲线 */}
        <path d={pathD} stroke="#1890ff" strokeWidth="2" fill="none" />
        {/* 刻度与标签（简化） */}
        {Array.from({ length: 5 }).map((_, i) => {
          const M = xMin + i * (xMax - xMin) / 4;
          const x = xScale(M);
          return (
            <g key={i}>
              <line x1={x} y1={height - padding.bottom} x2={x} y2={height - padding.bottom - 5} stroke="#aaa" />
              <text x={x - 10} y={height - padding.bottom + 16} fontSize="12" fill="#666">{M.toFixed(1)}</text>
            </g>
          );
        })}
        {Array.from({ length: 4 }).map((_, i) => {
          const t = i * yMax / 4;
          const y = yScale(t);
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={padding.left + 5} y2={y} stroke="#aaa" />
              <text x={8} y={y + 4} fontSize="12" fill="#666">{t.toFixed(1)}s</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// 曲线族双语标签统一来源于 exportUtils.mapCurveLabel

export default CalculationPage;
